import { formatLocalDate } from '../../utils/localDateTime';
import { showAppAlert } from '../../common/appDialog';
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GeopoliticalAnalysisResult, Page, NavParams, ChartItem } from '@types';
import { ICONS, RISK_LEVEL_COLORS } from '../constants';
import UnifiedMap from '../UnifiedMap';
import useGoogleMaps from '../useGoogleMaps';
import RiskChart from '../RiskChart';
import { Section, md, formatTimestamp, Disclaimer, Indicator } from './ReportShared';
import RecommendedSuitesDisplay from './RecommendedSuitesDisplay';
import { SectionBriefModal, type BriefType } from './SectionBriefModal';
import { sanitizeHtml } from '../../utils/safeMarkdown';
import { normalizeWhoMedicalSummary } from '../../../functions/src/common/medicalIntelligence';
import { cleanReportPromptText } from '../../../functions/src/common/reportPromptText';
import { getLeadershipStatus } from '../../../functions/src/common/leadershipStatus';
import { cleanCountryAssessments } from '../../../functions/src/common/countryAssessment';
import { isOrganizationAccess } from '../../common/organizationAccessMode';
import { getMapCoordinates } from '../../utils/mapCoordinates';
import { isUnitedStatesReport, US_ENTRY_SUMMARY, US_VISA_URL } from '../../common/usEntryGuidance';
import { filterDailyMicroEvents, dailyMicroEventMarker } from '../../utils/dailyMicroEvents';
import { eventCountry } from '../../utils/riskEventFeed';
import { normalizeISO3 } from '../../utils/geography';
import { quantitativeIndicatorGroups } from '../../utils/quantitativeIndicators';
import { mergeFullReportData } from '../../utils/reportDataMerge';
import { threatGroupMapDetails } from '../../utils/threatGroupMapDetails';
import { formatAcapsPopulation } from '../../utils/acapsPopulation';
import { enrichEconomicIntelligence } from '../../../functions/src/common/economicIntelligence';

// ─── Regional Consultant Contact Lookup ──────────────────────────────────────
const ISO3_REGION_MAP: Record<string, 'africa_middle_east' | 'americas_oceania' | 'uk_europe_asia'> = {
    // Africa
    DZA:'africa_middle_east',AGO:'africa_middle_east',BEN:'africa_middle_east',BWA:'africa_middle_east',
    BFA:'africa_middle_east',BDI:'africa_middle_east',CMR:'africa_middle_east',CPV:'africa_middle_east',
    CAF:'africa_middle_east',TCD:'africa_middle_east',COM:'africa_middle_east',COD:'africa_middle_east',
    COG:'africa_middle_east',DJI:'africa_middle_east',EGY:'africa_middle_east',GNQ:'africa_middle_east',
    ERI:'africa_middle_east',ETH:'africa_middle_east',GAB:'africa_middle_east',GMB:'africa_middle_east',
    GHA:'africa_middle_east',GIN:'africa_middle_east',GNB:'africa_middle_east',CIV:'africa_middle_east',
    KEN:'africa_middle_east',LSO:'africa_middle_east',LBR:'africa_middle_east',LBY:'africa_middle_east',
    MDG:'africa_middle_east',MWI:'africa_middle_east',MLI:'africa_middle_east',MRT:'africa_middle_east',
    MUS:'africa_middle_east',MAR:'africa_middle_east',MOZ:'africa_middle_east',NAM:'africa_middle_east',
    NER:'africa_middle_east',NGA:'africa_middle_east',RWA:'africa_middle_east',STP:'africa_middle_east',
    SEN:'africa_middle_east',SLE:'africa_middle_east',SOM:'africa_middle_east',ZAF:'africa_middle_east',
    SSD:'africa_middle_east',SDN:'africa_middle_east',SWZ:'africa_middle_east',TZA:'africa_middle_east',
    TGO:'africa_middle_east',TUN:'africa_middle_east',UGA:'africa_middle_east',ZMB:'africa_middle_east',
    ZWE:'africa_middle_east',
    // Middle East
    ARE:'africa_middle_east',BHR:'africa_middle_east',IRQ:'africa_middle_east',IRN:'africa_middle_east',
    ISR:'africa_middle_east',JOR:'africa_middle_east',KWT:'africa_middle_east',LBN:'africa_middle_east',
    OMN:'africa_middle_east',PSE:'africa_middle_east',QAT:'africa_middle_east',SAU:'africa_middle_east',
    SYR:'africa_middle_east',YEM:'africa_middle_east',TUR:'africa_middle_east',
    // Americas
    USA:'americas_oceania',CAN:'americas_oceania',MEX:'americas_oceania',GTM:'americas_oceania',
    BLZ:'americas_oceania',HND:'americas_oceania',SLV:'americas_oceania',NIC:'americas_oceania',
    CRI:'americas_oceania',PAN:'americas_oceania',CUB:'americas_oceania',DOM:'americas_oceania',
    HTI:'americas_oceania',JAM:'americas_oceania',TTO:'americas_oceania',BRB:'americas_oceania',
    BHS:'americas_oceania',LCA:'americas_oceania',VCT:'americas_oceania',GRD:'americas_oceania',
    ATG:'americas_oceania',DMA:'americas_oceania',KNA:'americas_oceania',BRA:'americas_oceania',
    ARG:'americas_oceania',COL:'americas_oceania',CHL:'americas_oceania',PER:'americas_oceania',
    VEN:'americas_oceania',BOL:'americas_oceania',ECU:'americas_oceania',URY:'americas_oceania',
    PRY:'americas_oceania',GUY:'americas_oceania',SUR:'americas_oceania',
    // Oceania
    AUS:'americas_oceania',NZL:'americas_oceania',PNG:'americas_oceania',FJI:'americas_oceania',
    SLB:'americas_oceania',VUT:'americas_oceania',WSM:'americas_oceania',TON:'americas_oceania',
    KIR:'americas_oceania',MHL:'americas_oceania',FSM:'americas_oceania',PLW:'americas_oceania',
    NRU:'americas_oceania',TUV:'americas_oceania',
    // UK
    GBR:'uk_europe_asia',
    // Europe
    ALB:'uk_europe_asia',AND:'uk_europe_asia',AUT:'uk_europe_asia',BLR:'uk_europe_asia',
    BEL:'uk_europe_asia',BIH:'uk_europe_asia',BGR:'uk_europe_asia',HRV:'uk_europe_asia',
    CYP:'uk_europe_asia',CZE:'uk_europe_asia',DNK:'uk_europe_asia',EST:'uk_europe_asia',
    FIN:'uk_europe_asia',FRA:'uk_europe_asia',DEU:'uk_europe_asia',GRC:'uk_europe_asia',
    HUN:'uk_europe_asia',ISL:'uk_europe_asia',IRL:'uk_europe_asia',ITA:'uk_europe_asia',
    LVA:'uk_europe_asia',LIE:'uk_europe_asia',LTU:'uk_europe_asia',LUX:'uk_europe_asia',
    MLT:'uk_europe_asia',MDA:'uk_europe_asia',MCO:'uk_europe_asia',MNE:'uk_europe_asia',
    NLD:'uk_europe_asia',MKD:'uk_europe_asia',NOR:'uk_europe_asia',POL:'uk_europe_asia',
    PRT:'uk_europe_asia',ROU:'uk_europe_asia',RUS:'uk_europe_asia',SMR:'uk_europe_asia',
    SRB:'uk_europe_asia',SVK:'uk_europe_asia',SVN:'uk_europe_asia',ESP:'uk_europe_asia',
    SWE:'uk_europe_asia',CHE:'uk_europe_asia',UKR:'uk_europe_asia',VAT:'uk_europe_asia',
    XKX:'uk_europe_asia',GEO:'uk_europe_asia',ARM:'uk_europe_asia',AZE:'uk_europe_asia',
    // Asia
    AFG:'uk_europe_asia',BGD:'uk_europe_asia',BTN:'uk_europe_asia',BRN:'uk_europe_asia',
    KHM:'uk_europe_asia',CHN:'uk_europe_asia',IND:'uk_europe_asia',IDN:'uk_europe_asia',
    JPN:'uk_europe_asia',KAZ:'uk_europe_asia',KGZ:'uk_europe_asia',LAO:'uk_europe_asia',
    MYS:'uk_europe_asia',MDV:'uk_europe_asia',MNG:'uk_europe_asia',MMR:'uk_europe_asia',
    NPL:'uk_europe_asia',PRK:'uk_europe_asia',PAK:'uk_europe_asia',PHL:'uk_europe_asia',
    SGP:'uk_europe_asia',KOR:'uk_europe_asia',LKA:'uk_europe_asia',TWN:'uk_europe_asia',
    TJK:'uk_europe_asia',THA:'uk_europe_asia',TLS:'uk_europe_asia',TKM:'uk_europe_asia',
    UZB:'uk_europe_asia',VNM:'uk_europe_asia',
};

const REGIONAL_CONTACTS = {
    africa_middle_east: { region: 'Africa & Middle East', title: 'Associate Director of Operations', name: 'Jennifer Ebelebe', email: 'jennifer.ebelebe@eprims.com' },
    americas_oceania:   { region: 'Americas & Oceania',   title: 'Associate Director of Operations', name: 'William Ruiz',      email: 'william.ruiz@eprims.com'       },
    uk_europe_asia:     { region: 'UK, Europe & Asia',    title: 'CEO / Director of Operations',     name: 'John Ferguson',    email: 'john.ferguson@eprims.com'      },
};

function getRegionalContact(iso3?: string) {
    if (!iso3) return null;
    const key = ISO3_REGION_MAP[iso3.toUpperCase()];
    return key ? REGIONAL_CONTACTS[key] : null;
}
// ─────────────────────────────────────────────────────────────────────────────

interface GeopoliticalAnalysisDisplayProps {
    result: GeopoliticalAnalysisResult;
    alerts?: any[];
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
    isDailyMicro?: boolean;
    readOnly?: boolean;
}

/** Coerces any value to a plain string safe for md.render(). Prevents
 *  'Input data should be a String' crashes when AI returns objects instead
 *  of strings for summary/text fields. */
const safeStr = (val: any): string => {
    if (typeof val === 'string') return val;
    if (val == null) return '';
    if (typeof val === 'object') {
        return val.text || val.content || val.summary || val.description || val.overview || '';
    }
    return String(val);
};

const validEconomicValue = (value: unknown): string | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    if (typeof value !== 'string') return null;
    const text = value.trim();
    return text && !/N\/A|USE VALUE FROM STRUCTURED DATA|undefined|null/i.test(text) ? text : null;
};

const gdpStatus = (economic: any, worldBank: any): string => {
    const growth = typeof worldBank?.gdpGrowth === 'number'
        ? `${worldBank.gdpGrowth}%`
        : validEconomicValue(economic?.gdpGrowth);
    const absolute = typeof worldBank?.gdpAbsolute === 'number'
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(worldBank.gdpAbsolute)
        : null;
    if (growth || absolute) return [growth && `Growth: ${growth}`, absolute && `GDP: ${absolute}`].filter(Boolean).join(' · ');
    return validEconomicValue(economic?.gdp) || 'Data unavailable';
};

const currencyStatus = (economic: any, worldBank: any): string => {
    const name = validEconomicValue(worldBank?.currency);
    const symbol = validEconomicValue(worldBank?.currencySymbol);
    if (name) return symbol && !name.endsWith(`(${symbol})`) ? `${name} (${symbol})` : name;
    const modelValue = typeof economic?.currency === 'string'
        ? economic.currency.replace(/^USE VALUE FROM STRUCTURED DATA:\s*/i, '')
        : economic?.currency;
    return validEconomicValue(modelValue) || 'Data unavailable';
};

/** Returns a hex color for a threat group type string — used for map markers and briefing panel accents. */
const getThreatGroupColor = (type: string): string => {
    const t = (type || '').toLowerCase();
    if (t.includes('terrorist') || t.includes('extremist') || t.includes('jihadist')) return '#ef4444';
    if (t.includes('criminal') || t.includes('cartel') || t.includes('gang')) return '#f97316';
    if (t.includes('insurgent') || t.includes('separatist') || t.includes('rebel') || t.includes('guerrilla')) return '#eab308';
    if (t.includes('militia') || t.includes('state') || t.includes('proxy') || t.includes('paramilitary')) return '#a855f7';
    return '#94a3b8';
};

const GeopoliticalAnalysisDisplay: React.FC<GeopoliticalAnalysisDisplayProps> = ({ result, alerts, onNavigate, isDailyMicro, readOnly }) => {
    const [activePmesiiTab, setActivePmesiiTab] = useState('political');
    const [showLocalizedSummary, setShowLocalizedSummary] = useState(false);
    const [focusedLocation, setFocusedLocation] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
    const [fitCountryRequest, setFitCountryRequest] = useState(0);
    const [selectedThreatGroup, setSelectedThreatGroup] = useState<any>(null);
    const [tgFocusedLocation, setTgFocusedLocation] = useState<{ lat: number; lng: number; zoom?: number } | null>(null);
    const [openBrief, setOpenBrief] = useState<BriefType | null>(null);
    const [briefDetailGroup, setBriefDetailGroup] = useState<any>(null);

    const { isLoaded } = useGoogleMaps();
    const cleaned = cleanCountryAssessments(cleanReportPromptText(mergeFullReportData(result))) as any;
    const dailyMicroMode = Boolean(isDailyMicro || ['DailyMicroReport', 'CountryRiskProfile'].includes(cleaned.reportType));
    const data = dailyMicroMode || cleaned.economicIndicators || cleaned.quantitativeIndicators?.worldBank
        ? enrichEconomicIntelligence(cleaned) : cleaned;
    const suppressSourceLinks = dailyMicroMode;
    if (data.leadershipOverview)
        data.leadershipOverview = { ...data.leadershipOverview, currentStatus: getLeadershipStatus(data.leadershipOverview) };
    const isUnitedStates = isUnitedStatesReport(data);
    const cdcTravelNotices = data.cdcTravelNotices;
    const cdcDestinationUrl = isUnitedStates
        ? 'https://wwwnc.cdc.gov/travel/destinations/traveler/none/united-states'
        : 'https://wwwnc.cdc.gov/travel/destinations/list';
    const whoMedicalSummary = normalizeWhoMedicalSummary(
        data.whoMedicalSummary || data.medicalInfo || data.healthData,
        data.country || data.countryName
    );
    const storedAcaps = data.quantitativeIndicators?.acaps;
    const humanitarianAlerts = data.humanitarianAlerts || (storedAcaps ? {
        acapsCount: storedAcaps.crisisCount ?? 0,
        totalAffected: storedAcaps.totalAffected ?? null,
        summary: storedAcaps.overallHumanitarianSummary || 'No active humanitarian crises reported by ACAPS.',
        topCrises: storedAcaps.topCrises ?? []
    } : {
        acapsCount: 0,
        totalAffected: null,
        summary: 'ACAPS data is unavailable for this report.',
        topCrises: []
    });
    const humanitarianSummary = dailyMicroMode
        ? String(humanitarianAlerts.summary || '').replace(/\bACAPS\b/gi, 'humanitarian monitoring')
        : humanitarianAlerts.summary;
    const [isRefreshing, setIsRefreshing] = useState(false);


    const handleManualRefresh = async () => {
        const targetCode = result.countryCode || result.iso3;
        
        if (!targetCode) {
            void showAppAlert("Insufficient metadata (country code) to trigger intelligence repair.");
            return;
        }
        
        setIsRefreshing(true);
        try {
            const functions = getFunctions();
            const triggerManualAnalysis = httpsCallable(functions, 'triggerManualCountryAnalysis');
            console.log(`🚀 Triggering manual repair for: ${targetCode}`);
            const response = await triggerManualAnalysis({ countryCode: targetCode });
            console.log("✅ Analysis triggered:", response.data);
            void showAppAlert("High-fidelity intelligence refresh started. This document will update automatically in a few moments.");
        } catch (error: any) {
            console.error("❌ Refresh Error:", error);
            void showAppAlert(`Failed to trigger refresh: ${error.message}`);
        } finally {
            setIsRefreshing(false);
        }
    };

    const infraMarkers = (data.infrastructureMapData || [])
        .filter((item: any) => {
            const lat = typeof item.lat === 'number' ? item.lat : (typeof item.coordinates?.lat === 'number' ? item.coordinates.lat : null);
            const lng = typeof item.lng === 'number' ? item.lng : (typeof item.coordinates?.lng === 'number' ? item.coordinates.lng : null);
            return lat !== null && lng !== null;
        })
        .map((item: any, index: number) => {
            const lat = typeof item.lat === 'number' ? item.lat : item.coordinates.lat;
            const lng = typeof item.lng === 'number' ? item.lng : item.coordinates.lng;
            return {
                ...item,
                id: `infra-${index}`, 
                lat, 
                lng, 
                name: item.name, 
                title: item.name,
                type: item.type, 
                description: item.description,
                summary: item.description || `Sector: ${item.type}`,
                color: 
                    item.type === 'Military Base' || item.type === 'Military' ? '#f43f5e' : 
                    item.type === 'Airport' ? '#0ea5e9' : 
                    item.type === 'Government Building' ? '#8b5cf6' : 
                    item.type === 'Hospital' ? '#10b981' : 
                    item.type === 'Police Region' ? '#3b82f6' : 
                    item.type === 'Energy' ? '#f59e0b' : 
                    item.type === 'Transport' ? '#64748b' : 
                    item.type === 'Telecom' ? '#ec4899' : 
                    '#10b981'
            };
        });
    
    const threatMarkers = (data.threatGroups || [])
        .filter((group: any) => getMapCoordinates(group.areaOfOperations || group))
        .map((group: any, index: number) => {
            const coords = getMapCoordinates(group.areaOfOperations || group)!;
            return {
                ...group,
                ...threatGroupMapDetails(group),
                id: `threat-${index}`,
                lat: coords.lat, 
                lng: coords.lng, 
                name: group.name, 
                title: group.name,
                type: 'ThreatGroup',
                description: `Type: ${group.type}. ${group.areaOfOperations?.description || group.summary || ''}`, 
                summary: `Type: ${group.type}. ${group.areaOfOperations?.description || group.summary || ''}`,
                color: '#000000'
            };
        });
        
    const embassyMarkers = (data.embassies || [])
        .filter((emb: any) => emb && typeof emb.lat === 'number' && typeof emb.lng === 'number')
        .map((emb: any, idx: number) => ({
            ...emb,
            id: `emb-${idx}`, 
            lat: emb.lat, 
            lng: emb.lng, 
            name: `Embassy: ${emb.country}`, 
            title: `Embassy: ${emb.country}`,
            type: 'Embassy', 
            description: emb.location, 
            summary: emb.location,
            color: '#7e22ce'
        }));

    const regionalMarkers = (data.regionalRisks || [])
        .filter((r: any) => {
            const coords = r.coordinates || r;
            return coords && typeof coords.lat === 'number' && typeof coords.lng === 'number';
        })
        .map((r: any, idx: number) => {
            const coords = r.coordinates || r;
            return {
                ...r,
                id: `reg-${idx}`, 
                lat: coords.lat, 
                lng: coords.lng, 
                name: r.region || r.location || 'Region', 
                title: `Regional Risk: ${r.region || r.location}`,
                type: 'RegionalRisk', 
                summary: `Risk Level: ${r.riskLevel || 'Elevated'}. Drivers: ${r.drivers?.join(', ') || r.description || 'N/A'}`,
                color: r.riskLevel === 'High' || r.riskLevel === 'Critical' ? '#ef4444' : '#f59e0b'
            };
        });

    const incidentMarkers = (data.securityIncidents || [])
        .filter((s: any) => getMapCoordinates(s))
        .map((s: any, idx: number) => {
            const coords = getMapCoordinates(s)!;
            return {
                ...s,
                id: `inc-${idx}`, 
                lat: coords.lat, 
                lng: coords.lng, 
                name: s.type || 'Incident', 
                title: `Incident: ${s.type}`,
                type: 'SecurityIncident', 
                summary: `${s.location || ''}: ${s.description}`,
                color: '#dc2626'
            };
        });

    // Handle both the single localized asset and the array of global assets
    const singleAssetMarker = (!!data.localizedAnalysis && typeof data.lat === 'number' && typeof data.lng === 'number') ? [{
        id: 'asset-localized',
        lat: data.lat,
        lng: data.lng,
        name: data.location_name || 'Project Asset',
        title: `Project Asset: ${data.location_name || ''}`,
        type: 'Asset',
        description: data.location_address || '',
        summary: `Strategic Asset: ${data.location_name || ''}. Risk Level: ${data.riskRating || 'N/A'}`,
        color: '#FFD700', // Gold
        isAsset: true
    }] : [];

    const globalAssetMarkers = (data.assets || [])
        .filter((a: any) => a && typeof a.lat === 'number' && typeof a.lng === 'number')
        .map((a: any, idx: number) => ({
            ...a,
            id: `global-asset-${idx}`,
            lat: a.lat,
            lng: a.lng,
            name: a.name,
            title: `Asset: ${a.name}`,
            type: 'Asset',
            description: a.location_address || '',
            summary: `Global Asset: ${a.name}. Type: ${a.type}`,
            color: '#FFD700', // Gold
            isAsset: true
        }));

    // Bridge live ECC risk alerts into map markers so Daily Micro reports from
    // the Risk Map show real-time country events on the Geospatial layer.
    const rawReportCountry = data.iso3 || data.countryCode || data.reportArea?.country || data.country || (result as any).countryIso3 || result.country || '';
    const reportIso3 = eventCountry({ country: rawReportCountry }) ||
        (/^[a-z]{2,3}$/i.test(rawReportCountry) ? normalizeISO3(rawReportCountry) : null);
    const embeddedDailyEvents = filterDailyMicroEvents(data, [
            ...(data.dailyEvents || []),
        ]).filter(event => !reportIso3 || !eventCountry(event) || eventCountry(event) === reportIso3);
    const linkedDailyEvents = filterDailyMicroEvents(data, alerts || [])
        .filter(event => !reportIso3 || eventCountry(event) === reportIso3);
    const eventCandidates = dailyMicroMode
        ? filterDailyMicroEvents(data, [...linkedDailyEvents, ...embeddedDailyEvents])
        : (alerts || []);
    const alertMarkers = eventCandidates
        .flatMap((event: any, index: number) => {
            const marker = dailyMicroEventMarker(event, index);
            return marker ? [marker] : [];
        });

    const allMarkers = [...infraMarkers, ...threatMarkers, ...embassyMarkers, ...regionalMarkers, ...incidentMarkers, ...singleAssetMarker, ...globalAssetMarkers];
    const reportMapMarkers = dailyMicroMode ? alertMarkers : [...allMarkers, ...alertMarkers];

    // ── Threat Group Intelligence Map ──────────────────────────────────────────
    const tgGroups: any[] = data.threatGroups || [];
    const tgMapMarkers = tgGroups
        .filter((g: any) => getMapCoordinates(g.areaOfOperations || g))
        .map((g: any, idx: number) => {
            const { lat, lng } = getMapCoordinates(g.areaOfOperations || g)!;
            const color = getThreatGroupColor(g.type);
            return {
                ...g,
                ...threatGroupMapDetails(g),
                id: `tg-map-${idx}`,
                lat,
                lng,
                name: g.name,
                title: g.name,
                type: 'ThreatGroup',
                summary: `${g.type || 'Unknown'} — ${g.threatLevel || ''} Threat. ${(g.summary || '').substring(0, 180)}`,
                color,
            };
        });

    const tgActivityMarkers = tgGroups.flatMap((group: any, groupIndex: number) =>
        (group.recentActivities || []).flatMap((activity: any, activityIndex: number) => {
            const coordinates = getMapCoordinates(activity);
            return coordinates ? [{
                ...activity,
                ...threatGroupMapDetails(activity),
                id: `tg-activity-${groupIndex}-${activityIndex}`,
                ...coordinates,
                name: activity.type || 'Threat Activity',
                title: activity.type || 'Threat Activity',
                type: 'SecurityIncident',
                summary: activity.description || `${group.name || 'Threat group'} activity`,
                color: '#ef4444',
            }] : [];
        })
    );
    const tgLocationMarkers = tgGroups.flatMap((group: any, groupIndex: number) =>
        (group.areaOfOperations?.locations || []).flatMap((location: any, locationIndex: number) => {
            const coordinates = getMapCoordinates(location);
            return coordinates ? [{
                ...location,
                ...threatGroupMapDetails(location),
                id: `tg-location-${groupIndex}-${locationIndex}`,
                ...coordinates,
                name: location.name || group.name || 'Operational Area',
                title: location.name || group.name || 'Operational Area',
                type: 'ThreatGroup',
                summary: `Operational area for ${group.name || 'threat group'}.`,
                color: getThreatGroupColor(group.type),
            }] : [];
        })
    );
    const tgDetailMarkers = [...tgActivityMarkers, ...tgLocationMarkers];
    const tgHeatPoints = tgDetailMarkers.map(marker => ({ lat: marker.lat, lng: marker.lng }));

    const tgCenterMarker = tgMapMarkers[0] || tgDetailMarkers[0];
    const tgCenter = tgCenterMarker
        ? { lat: tgCenterMarker.lat, lng: tgCenterMarker.lng }
        : { lat: 20, lng: 0 };
    const tgZoom = tgCenterMarker ? 5 : 3;

    // Determine map center: prefer static markers, fall back to first alert marker, then world view.
    const firstAlert = alertMarkers[0];
    const initialCenter = dailyMicroMode && firstAlert
        ? { lat: firstAlert.lat, lng: firstAlert.lng }
        : allMarkers.length > 0
            ? { lat: allMarkers[0].lat, lng: allMarkers[0].lng }
            : firstAlert
            ? { lat: firstAlert.lat, lng: firstAlert.lng }
            : { lat: 20, lng: 0 };
    const initialZoom = reportMapMarkers.length > 0 ? 4 : 2;

    const analystConfidence = data.analystConfidence || (typeof (result as any).confidence === 'object' ? (result as any).confidence : null);
    const pmesii = data.pmesii || data.PMESII;
    const trendingRaw = data.trendingStatus || data.trending || result.trending || "STABLE";
    const trending = typeof trendingRaw === 'string' ? trendingRaw : (trendingRaw as any).status || "STABLE";
    const displayedRecentEvents = dailyMicroMode ? eventCandidates : (data.recentEvents || []);

    const isLocalized = !!data.localizedAnalysis || 
                       (typeof data.summary === 'string' && data.summary.includes("LOCALIZED STRATEGIC INTELLIGENCE REPORT"));

    return (
        <>
        <div className="space-y-6 animate-in fade-in duration-500"> 
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3 mb-2">
                <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div className="flex-grow">
                    <p className="text-sm text-blue-800 font-bold">Project Intelligence Sync Active</p>
                    <p className="text-sm text-blue-700">
                        The background daily analysis will automatically update the active SRM project risk profile it is associated with.
                    </p>
                </div>
            </div>
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b-2 border-slate-900 pb-4 mb-6">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-slate-900 text-white text-[10px] font-black px-2 py-0.5 rounded tracking-tighter uppercase">IntellOptics ECC</span>
                        <span className="text-slate-500 font-bold text-[10px] tracking-widest uppercase">System: 0x77-ALPHA</span>
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight leading-none uppercase flex items-center gap-3">
                        {dailyMicroMode ? "Daily Country Analysis" : (data.title || (result as any).title || (data.country ? `State of the Country: ${data.country}` : 'Geopolitical Intelligence Dossier'))}
                        {isLocalized && (
                            <button 
                                onClick={() => setShowLocalizedSummary(true)}
                                className="bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[10px] font-bold px-2 py-1 rounded-sm border border-emerald-200 transition-colors cursor-pointer group flex items-center gap-1.5"
                                title="Click to view localized site summary"
                            >
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                                </span>
                                LOCALIZED SITE ANALYSIS
                            </button>
                        )}
                    </h2>
                    <div className="text-sm text-slate-500 font-medium mt-1 uppercase tracking-wider">
                        Geopolitical Analysis & Asset Monitoring
                    </div>
                </div>
                <div className="flex flex-col md:items-end">
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Document Registry</div>
                    <div className="text-sm font-bold text-slate-700">{dailyMicroMode && data.reportDate
                        ? `${formatLocalDate(data.reportDate, data.reportDate)}${data.timeZone ? ` · ${data.timeZone}` : ''}`
                        : formatTimestamp(data.analysis_date || data.date || (result as any).date) || formatLocalDate(new Date())}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                        {dailyMicroMode ? 'Analysis Prepared: ' : 'Last Refresh: '}<span className="text-slate-600 font-bold tracking-tighter">{formatTimestamp((dailyMicroMode && data.analysisPreparedAt) || data.last_refresh || data.lastUpdated || (result as any).lastUpdated || data.timestamp || (result as any).timestamp) || 'LIVE'}</span>
                    </div>
                </div>
            </div>

            <div className="bg-slate-50 p-5 rounded-lg border border-slate-200 mb-6 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight">Executive Summary</h3>
                    <div className="flex gap-2 items-center">
                        {isOrganizationAccess && !readOnly && <button
                            onClick={handleManualRefresh}
                            disabled={isRefreshing}
                            className={`px-3 py-1 rounded text-[10px] font-black tracking-widest uppercase border-2 transition-all ${
                                isRefreshing 
                                ? 'bg-slate-100 text-slate-400 border-slate-200' 
                                : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'
                            }`}
                        >
                            {isRefreshing ? '🔄 Refreshing...' : '⚡ Manual Intelligence Refresh'}
                        </button>}
                        <span className="text-[10px] font-black px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 uppercase">Classified: Internal Only</span>
                    </div>
                </div>
                <div className="prose prose-slate max-w-none text-slate-700 font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(safeStr(data.summary) || safeStr(data.executiveSummary) || safeStr(data.advisorySummary) || 'No executive summary available.') }} />
                
                {data.governmentStability && (
                    <div className="mt-4 bg-white p-3 rounded border border-slate-200">
                        <h4 className="text-sm font-bold text-slate-700 mb-1 uppercase">Government Stability Assessment</h4>
                        <div className="text-sm text-slate-600">{data.governmentStability}</div>
                    </div>
                )}
                
                {data.predictiveAnalysis && (
                    <div className="mt-4 bg-indigo-50 p-3 rounded border border-indigo-200">
                         <h4 className="text-sm font-bold text-indigo-800 mb-1 uppercase">Strategic Outlook (6-12 Months)</h4>
                         <div className="text-sm text-indigo-900" dangerouslySetInnerHTML={{ __html: md.render(
                             typeof data.predictiveAnalysis === 'string' ? data.predictiveAnalysis : 
                             (data.predictiveAnalysis as any)?.geopoliticalOutlook || 
                             (data.predictiveAnalysis as any)?.summary || ''
                         )}}/>
                    </div>
                )}
                
                {analystConfidence && (
                    <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col gap-3 text-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="font-semibold text-slate-600">Analyst Confidence:</span> 
                                <span className={`ml-2 px-2 py-0.5 rounded font-bold ${analystConfidence.score === 'High' ? 'bg-green-100 text-green-800' : analystConfidence.score === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                    {analystConfidence.score}
                                </span>
                            </div>
                            <div className="text-slate-500 italic text-xs">Gaps: {analystConfidence.dataGaps}</div>
                        </div>
                        {analystConfidence.justification && (
                            <div className="text-xs text-slate-600 bg-slate-100 p-2 rounded italic border-l-2 border-slate-300">
                                <strong>Justification:</strong> {analystConfidence.justification}
                            </div>
                        )}
                    </div>
                )}
                
                {trending && (
                    <div className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                        <span className="text-slate-500">Trending Status:</span>
                        <span className={`px-2 py-0.5 rounded ${trending.toLowerCase().includes('stable') ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                            {trending}
                        </span>
                    </div>
                )}
            </div>

            {/* Regional Consultant Contact Card — Deep Dive only */}
            {!dailyMicroMode && (() => {
                const iso3 = data.iso3 || (result as any).iso3 || (result as any).countryCode || data.countryCode;
                const contact = getRegionalContact(iso3);
                if (!contact) return null;
                return (
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl p-4 mb-6 flex items-start gap-4 shadow-sm">
                        <div className="flex-shrink-0 bg-indigo-700 text-white rounded-full w-11 h-11 flex items-center justify-center font-black text-sm uppercase tracking-tight shadow">
                            {contact.name.split(' ').map((n: string) => n[0]).join('')}
                        </div>
                        <div className="flex-grow">
                            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-500 mb-0.5">Regional Intelligence Consultant — {contact.region}</div>
                            <div className="text-sm font-black text-indigo-900">{contact.name}</div>
                            <div className="text-xs text-indigo-700 font-semibold">{contact.title}</div>
                            <a
                                href={`mailto:${contact.email}`}
                                className="text-xs text-indigo-600 hover:text-indigo-900 font-bold mt-1.5 inline-flex items-center gap-1.5 transition-colors group"
                            >
                                <svg className="w-3 h-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                {contact.email}
                            </a>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className="text-[9px] font-black uppercase tracking-widest text-indigo-400 leading-tight">
                                For intelligence<br />consultation &amp;<br />in-country briefings
                            </div>
                        </div>
                    </div>
                );
            })()}

            {data.riskFactors && data.riskFactors.length > 0 && (
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6 shadow-inner">
                    <h3 className="text-xs font-black text-slate-500 mb-3 uppercase flex items-center gap-2 tracking-widest">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                        Integrated Geopolitical Risk Matrix
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {data.riskFactors.map((risk: any, i: number) => {
                            const factorText = typeof risk === 'string' ? risk : risk.factor || risk.name || 'Risk Factor';
                            const score = typeof risk === 'object' ? risk.score : null;
                            const isCritical = score !== null && score >= 80;
                            const isHigh = score !== null && score >= 50;
                            
                            return (
                                <div key={i} className={`flex items-center justify-between bg-white p-2 rounded border ${isCritical ? 'border-red-200 shadow-sm' : 'border-slate-100'} text-sm`}>
                                    <div className={`flex items-start gap-2 ${isCritical ? 'text-red-900 font-bold' : 'text-slate-700 font-medium'}`}>
                                        <span className={`${isCritical ? 'text-red-500' : 'text-slate-400'} mt-0.5`}>•</span>
                                        {factorText}
                                    </div>
                                    {score !== null && (
                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter ${score >= 80 ? 'bg-red-100 text-red-700' : score >= 50 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                                            {score >= 80 ? 'CRITICAL' : score >= 50 ? 'HIGH' : 'LOW'} {score}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-3 text-[9px] text-slate-400 font-bold uppercase tracking-tight text-right">
                        Baseline Analysis // Normalized 0-100 High-Intensity Scale
                    </div>
                </div>
            )}
         
            {quantitativeIndicatorGroups(data.quantitativeIndicators || result.quantitativeIndicators).length > 0 && (
                <QuantitativeIndicatorsDisplay indicators={data.quantitativeIndicators || result.quantitativeIndicators} />
            )}

            {data.latestIncidentSummary && (
                <div className="bg-red-900/10 p-4 rounded-lg border border-red-900/20 mb-6 border-l-4 border-l-red-600 shadow-sm">
                    <h3 className="text-[10px] font-black text-red-700 mb-1 uppercase tracking-widest flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        High-Priority Regional Intelligence Alert
                    </h3>
                    <p className="text-sm text-red-900/80 font-medium leading-relaxed italic border-l border-red-900/10 pl-3">
                        "{data.latestIncidentSummary}"
                    </p>
                </div>
            )}
            <Section title={dailyMicroMode ? "Report-Day Event Map" : "Geospatial Intelligence Layer"} className="report-map-threat-section">
                <div className="report-map-threat-layout flex flex-col lg:flex-row gap-6 h-[600px]">
                    <div className="report-map-threat-map flex-grow rounded-xl overflow-hidden border border-slate-300 relative shadow-inner">
                        <UnifiedMap
                            isLoaded={isLoaded}
                            initialViewState={{ 
                                longitude: initialCenter.lng, 
                                latitude: initialCenter.lat, 
                                zoom: initialZoom 
                            }}
                            focusedLocation={focusedLocation}
                            theme="light"
                            interactive={true}
                            alerts={dailyMicroMode ? [] : alerts}
                            filterIso3={dailyMicroMode ? reportIso3 : ((result as any).countryIso3 || result.country)}
                            highlightIso3={dailyMicroMode ? reportIso3 : null}
                            fitCountryRequest={fitCountryRequest}
                            showGlobalAssets={!dailyMicroMode}
                            onNavigate={onNavigate}
                            hideSidebar={dailyMicroMode}
                            markers={reportMapMarkers}
                            heatmapPoints={dailyMicroMode ? undefined : data.heatmapPoints}
                            choroplethEnabled={dailyMicroMode || !!(data as any).globalRiskData}
                            riskData={dailyMicroMode ? [] : ((data as any).globalRiskData || [])}
                        />
                        <div className="absolute top-4 right-4 z-10">
                             <button 
                                onClick={() => {
                                    if (dailyMicroMode) {
                                        setFocusedLocation(null);
                                        setFitCountryRequest(value => value + 1);
                                    } else
                                        setFocusedLocation({ lat: initialCenter.lat, lng: initialCenter.lng, zoom: initialZoom });
                                }}
                                className="bg-white/90 backdrop-blur-sm p-2 rounded-lg shadow-lg border border-slate-200 hover:bg-white transition-all group"
                                title="Reset View"
                             >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-600 group-hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor font-bold">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                             </button>
                        </div>
                    </div>

                    <div className="report-map-threat-list w-full lg:w-80 bg-slate-900 rounded-xl border border-slate-700 overflow-hidden flex flex-col shadow-2xl">
                        <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex items-center justify-between">
                            <h4 className="text-[10px] font-black uppercase text-blue-400 tracking-[0.2em]">{dailyMicroMode ? 'Report-Day Events' : 'Regional Intelligence'}</h4>
                            <span className="bg-red-500/10 text-red-500 text-[9px] font-black px-1.5 py-0.5 rounded border border-red-500/20">{dailyMicroMode ? 'DAILY' : 'LIVE'}</span>
                        </div>
                        <div className="flex-grow overflow-y-auto p-4 custom-scrollbar space-y-3">
                            {!dailyMicroMode && (data.securityIncidents || []).length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest pl-1">Security Incidents</div>
                                    {data.securityIncidents.map((s: any, idx: number) => (
                                        <div 
                                            key={idx}
                                            onClick={() => s.coordinates && setFocusedLocation({ lat: s.coordinates.lat, lng: s.coordinates.lng, zoom: 12 })}
                                            className="report-print-item bg-slate-800/40 hover:bg-slate-800 p-3 rounded-lg border border-slate-700/50 transition-all cursor-pointer group"
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                                    s.severity === 'High' ? 'bg-red-500/20 text-red-400' : 
                                                    s.severity === 'Medium' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'
                                                }`}>
                                                    {s.type}
                                                </span>
                                                <span className="text-[8px] text-slate-500 font-bold">{s.location}</span>
                                            </div>
                                            <p className="text-xs text-slate-300 font-medium leading-snug line-clamp-2 brightness-90 group-hover:brightness-100">{s.description}</p>
                                            {!suppressSourceLinks && s.sourceUrl && s.sourceUrl !== 'N/A' && (
                                                <a 
                                                    href={s.sourceUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer" 
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-[8px] text-blue-400 hover:text-blue-300 font-black uppercase tracking-tighter mt-1 inline-block"
                                                >
                                                    ↗ View Primary Source
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!dailyMicroMode && (data.regionalRisks || []).length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest pl-1">Hotspots & Risks</div>
                                    {data.regionalRisks.map((r: any, idx: number) => (
                                        <div 
                                            key={idx}
                                            onClick={() => r.coordinates && setFocusedLocation({ lat: r.coordinates.lat, lng: r.coordinates.lng, zoom: 10 })}
                                            className="report-print-item bg-slate-800/40 hover:bg-slate-800 p-3 rounded-lg border border-slate-700/50 transition-all cursor-pointer group"
                                        >
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-[10px] font-bold text-slate-200">{r.region}</span>
                                                <span className={`text-[8px] font-black px-1 rounded uppercase ${
                                                    r.riskLevel === 'Critical' || r.riskLevel === 'High' ? 'text-red-400' : 'text-orange-400'
                                                }`}>{r.riskLevel}</span>
                                            </div>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {r.drivers?.slice(0, 2).map((d: string, i: number) => (
                                                    <span key={i} className="text-[8px] bg-slate-700/50 text-slate-400 px-1 py-0.5 rounded">{d}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* Live ECC alert events — shown when no static incident data is present */}
                            {(dailyMicroMode || (!(data.securityIncidents?.length) && !(data.regionalRisks?.length))) && alertMarkers.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest pl-1">{dailyMicroMode ? 'Dated Events' : 'Live Risk Events'}</div>
                                    {alertMarkers.map((marker: any) => (
                                        <div
                                            key={marker.id}
                                            onClick={() => setFocusedLocation({ lat: marker.lat, lng: marker.lng, zoom: 8 })}
                                            className="report-print-item bg-slate-800/40 hover:bg-slate-800 p-3 rounded-lg border border-slate-700/50 transition-all cursor-pointer group"
                                        >
                                            <div className="flex justify-between items-start mb-1">
                                                <span
                                                    className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                                                    style={{ backgroundColor: `${marker.color}20`, color: marker.color }}
                                                >
                                                    {marker.type}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-300 font-medium leading-snug line-clamp-2 brightness-90 group-hover:brightness-100">
                                                {marker.name}
                                            </p>
                                            {marker.date && <p className="text-[9px] text-slate-500 mt-1">{formatTimestamp(marker.date)}</p>}
                                            {marker.summary && marker.summary !== marker.name && (
                                                <p className="text-[9px] text-slate-500 mt-1 line-clamp-2">{marker.summary}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {(dailyMicroMode || (!(data.securityIncidents?.length) && !(data.regionalRisks?.length))) && !alertMarkers.length && (
                                <div className="text-center py-8">
                                    <div role="status" className="text-slate-400 text-[10px] italic">
                                        {dailyMicroMode
                                            ? eventCandidates.length ? 'No dated, mapped events were reported for this report day.' : 'No new events have been recorded within the last 24 hours.'
                                            : 'No real-time tactical events reported for this region.'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {!dailyMicroMode && <InfrastructureInventory data={data.infrastructureMapData || []} />}
            </Section>



            {displayedRecentEvents.length > 0 && (
                <Section title={dailyMicroMode ? "Report Day Events" : "Intelligence Feed: Recent Developments"}>
                    <div className="space-y-4">
                        {displayedRecentEvents.map((event: any, i: number) => (
                            <div key={i} className="report-print-item flex flex-col sm:grid sm:grid-cols-[7rem_minmax(0,1fr)] gap-2 sm:gap-4 p-4 bg-white border rounded-lg shadow-sm border-l-4 border-l-blue-600 transition-hover hover:shadow-md">
                                <div className="text-xs font-bold text-slate-400 uppercase tracking-tighter sm:pt-1 break-words">
                                    {formatTimestamp(event.date) || 'RECENT'}
                                </div>
                                <div className="min-w-0">
                                    <h5 className="font-bold text-slate-800 text-sm break-words">{event.title}</h5>
                                    <p className="text-sm text-slate-600 mt-1 leading-relaxed break-words">{event.description || event.summary}</p>
                                    {event.impact && (
                                        <div className="mt-2 text-[10px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded inline-block uppercase">
                                            Impact: {event.impact}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
            
            {data.politicalParties && data.politicalParties.length > 0 && (
                <Section title="Political Landscape">
                    <div className="flex justify-end mb-3">
                        {!dailyMicroMode && <button
                            onClick={() => setOpenBrief('political')}
                            className="flex items-center gap-1.5 text-[10px] font-black text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-wider"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            View Full Brief
                        </button>}
                    </div>

                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-slate-900 text-white uppercase text-[10px] font-black tracking-widest">
                                <tr>
                                    <th className="px-4 py-3">Party Name</th>
                                    <th className="px-4 py-3">Stance / Ideology</th>
                                    <th className="px-4 py-3 text-right">Influence</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {data.politicalParties.map((p: any, i: number) => (
                                    <tr key={i} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-4 py-3 font-bold text-slate-900">{p.name}</td>
                                        <td className="px-4 py-3 text-slate-600">{p.orientation || p.stance}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                                p.influenceLevel === 'High' || p.influence === 'High' ? 'bg-red-100 text-red-700' : 
                                                p.influenceLevel === 'Med' || p.influence === 'Medium' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                                {p.influenceLevel || p.influence}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data.politicalParties[0]?.summary && (
                        <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs text-slate-600 leading-relaxed italic">
                            {data.politicalParties[0].summary}
                        </div>
                    )}
                </Section>
            )}

            {data.economicIndicators && (
                <Section title="Economic Intelligence">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <Indicator label="GDP Status" value={gdpStatus(data.economicIndicators, data.quantitativeIndicators?.worldBank)} />
                        <Indicator label="Inflation" value={typeof data.quantitativeIndicators?.worldBank?.inflation === 'number' ? `${data.quantitativeIndicators.worldBank.inflation}%` : validEconomicValue(data.economicIndicators.inflation) || 'Data unavailable'} />
                        <Indicator label="Unemployment" value={typeof data.quantitativeIndicators?.worldBank?.unemployment === 'number' ? `${data.quantitativeIndicators.worldBank.unemployment}%` : validEconomicValue(data.economicIndicators.unemployment) || 'Data unavailable'} />
                        <Indicator label="Currency" value={currencyStatus(data.economicIndicators, data.quantitativeIndicators?.worldBank)} />
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Economic Headwinds & Analysis</h5>
                        <p className="text-sm text-slate-700 leading-relaxed">{data.economicIndicators.summary}</p>
                    </div>
                </Section>
            )}

            {data.medicalInfo && (
                <Section title="Medical & Health Intelligence">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="bg-white border-2 border-emerald-900 p-5 rounded-2xl shadow-xl">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.691.34a2 2 0 00-1.109 1.788V19.5a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5v-4.63a2 2 0 00-1.109-1.788l-.691-.34a6 6 0 00-3.86-.517l-2.387.477a2 2 0 00-1.022.547l-.396.396A2 2 0 002 16.828V19.5c0 1.105.895 2 2 2h16c1.105 0 2-.895 2-2v-2.672a2 2 0 00-.586-1.414l-.396-.396z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11V3m0 0l-3 3m3-3l3 3" /></svg>
                                </div>
                                <div>
                                    <h5 className="text-sm font-black text-slate-900 uppercase">{dailyMicroMode ? 'Health Advisories' : 'Health Advisories (WHO/CDC)'}</h5>
                                    <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest">Medical Readiness Level</p>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">{dailyMicroMode ? 'Health Alert Status' : 'WHO Alert Status'}</span>
                                    <div className="text-sm font-bold text-slate-800">{data.medicalInfo.who_alerts}</div>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">{dailyMicroMode ? 'Travel Health Notice' : 'CDC Travel Notice'}</span>
                                    <div className="text-sm font-bold text-slate-800">
                                        {cdcTravelNotices?.notices?.length
                                            ? cdcTravelNotices.notices.map((notice: any, index: number) => suppressSourceLinks
                                                ? <span key={index} className="block">{notice.title}</span>
                                                : <a key={notice.url} href={notice.url} target="_blank" rel="noopener noreferrer" className="block text-blue-700 underline">{notice.title}</a>)
                                            : cdcTravelNotices?.checkedAt
                                                ? 'No matching notices in the CDC feed when checked.'
                                                : 'Current notice status not verified in this report.'}
                                    </div>
                                    {!suppressSourceLinks && <a href="https://wwwnc.cdc.gov/travel/notices" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline">Check current CDC notices</a>}
                                </div>
                            </div>
                        </div>

                        <div className="bg-emerald-50 border border-emerald-200 p-5 rounded-2xl">
                             <h5 className="text-[10px] font-black text-emerald-800 uppercase tracking-widest mb-4">Infrastructure & Requirements</h5>
                             <div className="space-y-4">
                                <div>
                                    <span className="text-[10px] font-black text-emerald-600/60 uppercase tracking-widest block mb-1">Facility Standards</span>
                                    <p className="text-xs text-emerald-900 font-medium">{data.medicalInfo.medical_infrastructure}</p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-black text-emerald-600/60 uppercase tracking-widest block mb-1">Vaccination Guidance</span>
                                    <p className="text-xs text-emerald-900 font-bold">Recommendations and requirements depend on the traveler and itinerary.</p>
                                    {!suppressSourceLinks && <a href={cdcDestinationUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 underline">Check current CDC destination guidance</a>}
                                </div>
                             </div>
                        </div>
                    </div>
                </Section>
            )}

            {data.travelAdvisory && (
                <Section title="Travel & Immigration Intelligence">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">US State Dept Advisory</span>
                            <div className={`text-xl font-black uppercase ${
                                data.travelAdvisory.us_state_dept_level.includes('4') ? 'text-red-600' :
                                data.travelAdvisory.us_state_dept_level.includes('3') ? 'text-orange-600' : 'text-emerald-600'
                            }`}>
                                {data.travelAdvisory.us_state_dept_level}
                            </div>
                            <p className="text-[10px] text-slate-500 font-medium mt-2 leading-relaxed">
                                {data.travelAdvisory.summary}
                            </p>
                        </div>

                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-700">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest block mb-1">Evacuation Status</span>
                            <div className="text-sm font-black text-white uppercase tracking-tight mb-2">
                                {data.travelAdvisory.evacuation_status}
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Visa Requirements</span>
                            <p className="text-xs text-slate-700 font-bold leading-relaxed mb-2">
                                {isUnitedStates ? US_ENTRY_SUMMARY : data.visaRequirements?.summary}
                            </p>
                            {data.visaRequirements?.restricted_nationalities && data.visaRequirements.restricted_nationalities.length > 0 && (
                                <div className="text-[10px] text-red-600 font-black uppercase">
                                    Restrictions: {data.visaRequirements.restricted_nationalities.join(', ')}
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            )}

            {data.leadershipOverview && (
                <Section title="Leadership & Governance">
                    <div className="flex justify-end mb-3">
                        {!dailyMicroMode && <button
                            onClick={() => setOpenBrief('keyFigures')}
                            className="flex items-center gap-1.5 text-[10px] font-black text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-wider"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            View Key Figures Brief
                        </button>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-slate-900 p-4 rounded-lg text-white border border-slate-700 shadow-xl">
                            <h5 className="text-xs font-black uppercase text-blue-400 mb-2 tracking-widest text-[10px]">Leadership Status</h5>
                            <div className="text-lg font-bold mb-4 border-b border-slate-700 pb-2 break-words leading-snug">{data.leadershipOverview.currentStatus}</div>
                            <div className="space-y-4">
                                <div>
                                    <h6 className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Head of State / Government</h6>
                                    <p className="text-sm text-slate-200 leading-relaxed font-bold mt-1">{data.leadershipOverview?.headOfState || (result.leadershipOverview && result.leadershipOverview.headOfState) || 'N/A'}</p>
                                </div>
                                <div>
                                    <h6 className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Political Structure</h6>
                                    <p className="text-sm text-slate-200 leading-relaxed font-medium mt-1">{data.leadershipOverview?.politicalStructure || (result.leadershipOverview && result.leadershipOverview.politicalStructure) || 'N/A'}</p>
                                </div>
                                {data.leadershipOverview?.stabilityOutlook && (
                                    <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                                        <h6 className="text-[10px] font-black uppercase text-indigo-400 tracking-wider">Stability Outlook</h6>
                                        <p className="text-xs text-slate-300 mt-1">{data.leadershipOverview.stabilityOutlook}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                            <h5 className="text-xs font-black uppercase text-slate-500 mb-3 tracking-widest text-[10px]">Government Apparatus Overview</h5>
                            <div className="space-y-4">
                                {data.governmentApparatus?.executive && (
                                    <div className="border-l-2 border-blue-400 pl-3">
                                        <h6 className="text-[10px] font-black uppercase text-slate-500">Executive Branch</h6>
                                        <p className="text-xs text-slate-700 mt-0.5">{data.governmentApparatus.executive}</p>
                                    </div>
                                )}
                                {data.governmentApparatus?.legislative && (
                                    <div className="border-l-2 border-indigo-400 pl-3">
                                        <h6 className="text-[10px] font-black uppercase text-slate-500">Legislative Branch</h6>
                                        <p className="text-xs text-slate-700 mt-0.5">{data.governmentApparatus.legislative}</p>
                                    </div>
                                )}
                                {data.governmentApparatus?.judicial && (
                                    <div className="border-l-2 border-purple-400 pl-3">
                                        <h6 className="text-[10px] font-black uppercase text-slate-500">Judicial Branch</h6>
                                        <p className="text-xs text-slate-700 mt-0.5">{data.governmentApparatus.judicial}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                     {data.leadershipOverview?.keyFigures && data.leadershipOverview.keyFigures.length > 0 && (
                        <div className="space-y-3">
                            <h5 className="text-sm font-bold text-slate-700 uppercase">Key Political Figures</h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {data.leadershipOverview.keyFigures.map((figure: any, i: number) => (
                                    <div key={i} className="bg-white p-3 border rounded shadow-sm">
                                        <div className="font-bold text-slate-900">{figure.name}</div>
                                        <div className="text-xs text-blue-600 font-bold uppercase mb-1">{figure.role}</div>
                                        <div className="text-xs text-slate-600 line-clamp-3">{figure.bio}</div>
                                        <div className="mt-2 text-[10px] font-bold text-slate-400 uppercase">Influence: {figure.influence}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {showLocalizedSummary && isLocalized && (
                        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
                            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden animate-in zoom-in-95 duration-300">
                                <div className="bg-slate-900 p-6 text-white flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-black uppercase tracking-tight">Localized Asset Intelligence</h3>
                                        <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Project ID: {data.projectId || 'INTERNAL'}</p>
                                    </div>
                                    <button 
                                        onClick={() => setShowLocalizedSummary(false)}
                                        className="p-2 hover:bg-white/10 rounded-full transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                <div className="p-8 space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Risk Rating</span>
                                            <span className={`text-lg font-black uppercase ${
                                                data.riskRating === 'Critical' ? 'text-red-600' :
                                                data.riskRating === 'High' ? 'text-orange-600' :
                                                data.riskRating === 'Medium' ? 'text-blue-600' : 'text-emerald-600'
                                            }`}>
                                                {data.riskRating || 'Standard'}
                                            </span>
                                        </div>
                                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Asset Status</span>
                                            <span className="text-lg font-black text-slate-900 uppercase">Operational</span>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Location Snapshot</span>
                                        <div className="text-sm font-bold text-slate-700 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                            {data.location_name}: {data.location_address}
                                        </div>
                                    </div>

                                    <div>
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Intelligence Summary</span>
                                        <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                            {data.summary || 'Localized intelligence sweep complete. Direct asset proximity analysis indicates consistent risk profile with regional updates.'}
                                        </p>
                                    </div>

                                    <button 
                                        onClick={() => setShowLocalizedSummary(false)}
                                        className="w-full py-4 bg-slate-900 hover:bg-black text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-xl hover:shadow-2xl active:scale-[0.98]"
                                    >
                                        Acknowledge Intelligence
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {data.leadershipOverview?.politicalParties && Array.isArray(data.leadershipOverview.politicalParties) && (
                        <div className="col-span-full mt-6 pt-4 border-t border-slate-200">
                            <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Dominant Political Parties & Coalitions</h5>
                            <div className="flex flex-wrap gap-2">
                                {data.leadershipOverview.politicalParties.map((p: string, idx: number) => (
                                    <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-[11px] font-bold rounded-full border border-blue-100 shadow-sm">
                                        {p}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </Section>
            )}

            {(data.governmentStructure || data.governmentApparatus) && (
                <Section title="Systemic Institutional Framework">
                    {/* ── Navigate-to-Diagram button ── */}
                    <div className="flex items-center justify-between mb-5">
                        {!dailyMicroMode && <p className="text-xs text-slate-500 italic">
                            Structured data sourced from World Bank WGI • Freedom House • WJP Rule of Law Index
                        </p>}
                        <button
                            onClick={() => onNavigate && onNavigate({ page: 'GovernanceStructure', params: {
                                reportData: data,
                                result: result,
                                country: data.country || result?.country,
                            } })}

                            className="flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 border border-slate-600 hover:border-blue-500 text-white text-[11px] font-black uppercase tracking-widest rounded-lg transition-all shadow-lg hover:shadow-blue-900/30 group"
                        >
                            <span className="text-base">🏛️</span>
                            View Governance Diagram
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-blue-400 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>

                    {/* ── World Bank WGI Score Bars ── */}
                    {data.worldGovernanceIndicators && (
                        <div className="mb-5 bg-slate-900 rounded-xl p-4 border border-slate-700">
                            <div className="flex items-center justify-between mb-3">
                                <h5 className="text-[10px] font-black uppercase tracking-widest text-blue-400">
                                    {dailyMicroMode ? 'Governance Indicators' : 'World Bank — Worldwide Governance Indicators'}
                                </h5>
                                {!suppressSourceLinks && <a
                                    href={`https://info.worldbank.org/governance/wgi/Home/Reports`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="text-[9px] text-slate-500 hover:text-blue-400 transition-colors"
                                >
                                    ↗ WGI Source
                                </a>}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {[
                                    { key: 'ruleOfLaw',          label: 'Rule of Law',              icon: '⚖️' },
                                    { key: 'govEffectiveness',   label: 'Gov. Effectiveness',       icon: '🏛️' },
                                    { key: 'controlOfCorruption',label: 'Control of Corruption',    icon: '🔒' },
                                    { key: 'voiceAccountability',label: 'Voice & Accountability',   icon: '📢' },
                                    { key: 'politicalStability', label: 'Political Stability',       icon: '🕊️' },
                                    { key: 'regulatoryQuality',  label: 'Regulatory Quality',       icon: '📋' },
                                ].map(({ key, label, icon }) => {
                                    const rawVal = data.worldGovernanceIndicators?.[key];
                                    const val = typeof rawVal === 'number' ? rawVal : null;
                                    const pct = val != null ? Math.round((val + 2.5) / 5 * 100) : null;
                                    const colour = pct == null ? 'bg-slate-600' : pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-yellow-400' : pct >= 25 ? 'bg-orange-500' : 'bg-red-500';
                                    const textColour = pct == null ? 'text-slate-500' : pct >= 75 ? 'text-emerald-400' : pct >= 50 ? 'text-yellow-400' : pct >= 25 ? 'text-orange-400' : 'text-red-400';
                                    return (
                                        <div key={key} className="space-y-1">
                                            <div className="flex justify-between items-center">
                                                <span className="text-[10px] font-bold text-slate-400">{icon} {label}</span>
                                                <span className={`text-[10px] font-black ${textColour}`}>
                                                    {pct != null ? `${pct}th pct.` : 'N/A'}
                                                </span>
                                            </div>
                                            <div className="w-full bg-slate-700 h-1.5 rounded-full overflow-hidden">
                                                {pct != null && <div className={`h-full rounded-full transition-all duration-700 ${colour}`} style={{ width: `${pct}%` }} />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* ── Freedom House & WJP ── */}
                    {(data.freedomHouseScore != null || data.ruleOfLawIndex != null) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                            {data.freedomHouseScore != null && (
                                <div className="bg-slate-900 border border-slate-700 rounded-xl p-3">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">Freedom House Score</span>
                                        {!suppressSourceLinks && <a href="https://freedomhouse.org/report/freedom-world" target="_blank" rel="noopener noreferrer" className="text-[9px] text-slate-500 hover:text-indigo-400">↗</a>}
                                    </div>
                                    <div className="text-xl font-black text-white">{data.freedomHouseScore} <span className="text-sm text-slate-500">/ 100</span></div>
                                    <div className="w-full bg-slate-700 h-1.5 rounded-full mt-2 overflow-hidden">
                                        <div className={`h-full rounded-full ${data.freedomHouseScore >= 70 ? 'bg-emerald-500' : data.freedomHouseScore >= 40 ? 'bg-yellow-400' : 'bg-red-500'}`} style={{ width: `${data.freedomHouseScore}%` }} />
                                    </div>
                                    <p className="text-[9px] text-slate-500 mt-1">{data.freedomHouseScore >= 70 ? '🟢 Free' : data.freedomHouseScore >= 40 ? '🟡 Partly Free' : '🔴 Not Free'}</p>
                                </div>
                            )}
                            {data.ruleOfLawIndex != null && (
                                <div className="bg-slate-900 border border-slate-700 rounded-xl p-3">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-[10px] font-black uppercase text-purple-400 tracking-widest">WJP Rule of Law Index</span>
                                        {!suppressSourceLinks && <a href="https://worldjusticeproject.org/rule-of-law-index/" target="_blank" rel="noopener noreferrer" className="text-[9px] text-slate-500 hover:text-purple-400">↗</a>}
                                    </div>
                                    <div className="text-xl font-black text-white">{Number(data.ruleOfLawIndex).toFixed(2)} <span className="text-sm text-slate-500">/ 1.00</span></div>
                                    <div className="w-full bg-slate-700 h-1.5 rounded-full mt-2 overflow-hidden">
                                        <div className={`h-full rounded-full ${data.ruleOfLawIndex >= 0.7 ? 'bg-emerald-500' : data.ruleOfLawIndex >= 0.4 ? 'bg-yellow-400' : 'bg-red-500'}`} style={{ width: `${data.ruleOfLawIndex * 100}%` }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Branch Cards ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {(data.governmentStructure?.judicial || data.governmentApparatus?.judicial) && (
                            <div className="p-3 bg-white border rounded shadow-sm border-b-2 border-b-purple-500">
                                <h5 className="text-[10px] font-black uppercase text-slate-500 mb-1">⚖️ Judicial Branch</h5>
                                <p className="text-xs text-slate-700">{data.governmentStructure?.judicial || data.governmentApparatus?.judicial}</p>
                            </div>
                        )}
                        {(data.governmentStructure?.intelligence || data.governmentApparatus?.intelligence) && (
                            <div className="p-3 bg-white border rounded shadow-sm border-b-2 border-b-indigo-500">
                                <h5 className="text-[10px] font-black uppercase text-slate-500 mb-1">🔭 Intelligence Agencies</h5>
                                <p className="text-xs text-slate-700">{data.governmentStructure?.intelligence || data.governmentApparatus?.intelligence}</p>
                            </div>
                        )}
                        {(data.governmentStructure?.military || data.governmentApparatus?.military) && (
                            <div className="p-3 bg-white border rounded shadow-sm border-b-2 border-b-red-500">
                                <h5 className="text-[10px] font-black uppercase text-slate-500 mb-1">🎖️ Military Oversight</h5>
                                <p className="text-xs text-slate-700">{data.governmentStructure?.military || data.governmentApparatus?.military}</p>
                            </div>
                        )}
                        {data.governmentStructure?.lawEnforcement && (
                            <div className="p-3 bg-white border rounded shadow-sm border-b-2 border-b-emerald-500">
                                <h5 className="text-[10px] font-black uppercase text-slate-500 mb-1">🚔 Law Enforcement</h5>
                                <p className="text-xs text-slate-700">{data.governmentStructure.lawEnforcement}</p>
                            </div>
                        )}
                    </div>
                    {data.governmentStructure?.overview && (
                        <div className="mt-4 p-4 bg-slate-50 border rounded text-sm text-slate-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: md.render(safeStr(data.governmentStructure.overview)) }} />
                    )}
                </Section>
            )}


            {humanitarianAlerts && (
                <Section title={dailyMicroMode ? "Humanitarian Crisis Monitor" : "ACAPS Humanitarian Crisis Monitor"}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-1 space-y-4">
                            <div className="bg-red-900 border border-red-700 rounded-xl p-5 shadow-xl">
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-[10px] font-black uppercase text-red-400 tracking-[0.2em]">Crisis Intensity</span>
                                    <span className="p-1.5 bg-red-500 rounded text-white font-black text-xs">{humanitarianAlerts.acapsCount || 0} Active Crises</span>
                                </div>
                                <div className="text-3xl font-black text-white mb-1">
                                    {formatAcapsPopulation(humanitarianAlerts.totalAffected, true)}
                                </div>
                                <div className="text-[10px] font-bold text-red-300 uppercase tracking-widest">Total Population Affected</div>
                                <div className="mt-4 pt-4 border-t border-red-800">
                                    <p className="text-xs text-red-100 italic leading-relaxed">
                                        {humanitarianSummary || 'Humanitarian conditions remain stable based on current institutional reporting.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                                <h4 className="text-[10px] font-black uppercase text-slate-800 tracking-wider">Priority Humanitarian Events</h4>
                                {!suppressSourceLinks && <a href="https://www.acaps.org" target="_blank" rel="noopener noreferrer" className="text-[9px] font-bold text-blue-600 hover:underline">ACAPS Data Portal &rarr;</a>}
                            </div>
                            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4 overflow-y-auto max-h-[250px]">
                                {humanitarianAlerts.topCrises?.length > 0 ? (
                                    humanitarianAlerts.topCrises.map((crisis: any, i: number) => (
                                        <div key={i} className="p-3 bg-slate-50 border border-slate-100 rounded-lg flex flex-col justify-between">
                                            <div>
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="text-xs font-black text-slate-800 line-clamp-1">{crisis.name}</span>
                                                    <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${
                                                        crisis.status?.toLowerCase().includes('high') ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                                                    }`}>
                                                        {crisis.type}
                                                    </span>
                                                </div>
                                                <p className="text-[10px] text-slate-600 line-clamp-2 leading-tight mb-2">{crisis.impact || 'Assessment of operational impact pending.'}</p>
                                            </div>
                                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                                                Pop. Affected: {formatAcapsPopulation(crisis.affected)}
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-span-full flex items-center justify-center h-24 text-slate-400 text-xs italic">
                                        No specific crisis events identified in institutional baseline.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Section>
            )}

            {pmesii && (
                <Section title="PMESII-PT Analysis">
                    <div className="analysis-screen-only border-b border-slate-200 mb-4 overflow-x-auto">
                        <nav className="-mb-px flex space-x-4">
                            {Object.keys(pmesii).filter(key => {
                                const content = (pmesii as any)[key];
                                if (!content) return false;
                                const desc = typeof content === 'string' ? content : content.description;
                                return desc && desc.length > 10 && !desc.includes("No specific") && !desc.includes("N/A");
                            }).map((key) => (
                                <button
                                    key={key}
                                    onClick={() => setActivePmesiiTab(key)}
                                    className={`whitespace-nowrap pb-2 px-1 border-b-2 font-medium text-sm capitalize transition-colors ${
                                        activePmesiiTab === key
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                    }`}
                                >
                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                </button>
                            ))}
                        </nav>
                    </div>
                    <div className="analysis-screen-only bg-white border rounded-xl overflow-hidden shadow-sm">
                        <div className="p-6">
                            <div className="prose prose-slate max-w-none text-slate-700 font-medium leading-relaxed mb-6">
                                <div dangerouslySetInnerHTML={{ __html: md.render(
                                    typeof (pmesii as any)[activePmesiiTab] === 'string' 
                                        ? (pmesii as any)[activePmesiiTab] 
                                        : (pmesii as any)[activePmesiiTab]?.description || ''
                                ) }} />
                            </div>

                            {!suppressSourceLinks && typeof (pmesii as any)[activePmesiiTab] === 'object' && (pmesii as any)[activePmesiiTab]?.sources?.length > 0 && (
                                <div className="mt-6 pt-6 border-t border-slate-100">
                                    <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor font-bold">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.232.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                        Intelligence Sources & References
                                    </h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {(pmesii as any)[activePmesiiTab].sources.map((src: string, i: number) => (
                                            <div key={i} className="flex flex-col p-3 bg-slate-50 border border-slate-100 rounded-lg">
                                                <span className="text-sm font-bold text-slate-800">{src}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="analysis-print-only space-y-4">
                        {Object.entries(pmesii).filter(([, content]: [string, any]) => {
                            const description = typeof content === 'string' ? content : content?.description;
                            return description && description.length > 10 && !description.includes('No specific') && !description.includes('N/A');
                        }).map(([key, content]: [string, any]) => (
                            <div key={key} className="bg-white border rounded-xl p-5 shadow-sm">
                                <h4 className="mb-3 text-sm font-black uppercase tracking-wider text-blue-700">{key.replace(/([A-Z])/g, ' $1').trim()}</h4>
                                <div className="prose prose-slate max-w-none text-slate-700 font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: md.render(typeof content === 'string' ? content : content.description || '') }} />
                                {!suppressSourceLinks && typeof content === 'object' && content?.sources?.length > 0 && (
                                    <div className="mt-4 border-t border-slate-100 pt-4">
                                        <h5 className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">Intelligence Sources &amp; References</h5>
                                        <ul className="space-y-1 text-xs text-slate-700">
                                            {content.sources.map((source: any, index: number) => <li key={index}>{typeof source === 'object' ? source.stringValue || JSON.stringify(source) : source}</li>)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {data.economicIndicators && (
                <Section title={dailyMicroMode ? "Economic Intelligence" : "Economic Intelligence & World Bank Data"}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {[
                                    { label: 'GDP Growth', value: typeof data.quantitativeIndicators?.worldBank?.gdpGrowth === 'number' ? `${data.quantitativeIndicators.worldBank.gdpGrowth}%` : validEconomicValue(data.economicIndicators.gdpGrowth), icon: '📈' },
                                    { label: 'Inflation', value: typeof data.quantitativeIndicators?.worldBank?.inflation === 'number' ? `${data.quantitativeIndicators.worldBank.inflation}%` : validEconomicValue(data.economicIndicators.inflation), icon: '💸' },
                                    { label: 'Unemployment', value: typeof data.quantitativeIndicators?.worldBank?.unemployment === 'number' ? `${data.quantitativeIndicators.worldBank.unemployment}%` : validEconomicValue(data.economicIndicators.unemployment), icon: '👥' },
                                    { label: 'Debt to GDP', value: typeof data.quantitativeIndicators?.worldBank?.debt_to_gdp === 'number' ? `${data.quantitativeIndicators.worldBank.debt_to_gdp}%` : validEconomicValue(data.economicIndicators.debtToGdp), icon: '⚖️' },
                                    { label: 'GNI Per Capita', value: typeof data.quantitativeIndicators?.worldBank?.gni_per_capita === 'number' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(data.quantitativeIndicators.worldBank.gni_per_capita) : validEconomicValue(data.economicIndicators.gniPerCapita), icon: '💰' },
                                    { label: 'Foreign Reserves', value: typeof data.quantitativeIndicators?.worldBank?.foreignReserves === 'number' ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 2 }).format(data.quantitativeIndicators.worldBank.foreignReserves) : validEconomicValue(data.economicIndicators.foreignReserves), icon: '🏦' },
                                ].map((stat, i) => (
                                    <div key={i} className="bg-white p-3 border rounded-lg shadow-sm border-b-2 border-b-slate-300">
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.icon} {stat.label}</div>
                                        <div className="text-sm font-black text-slate-900">{stat.value || 'Data unavailable'}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-blue-50 p-5 rounded-xl border border-blue-100 shadow-inner">
                            <h5 className="text-[10px] font-black text-blue-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                {dailyMicroMode ? 'Strategic Economic Context' : 'World Bank & IMF Strategic Context'}
                            </h5>
                            <div className="prose prose-sm prose-slate max-w-none text-blue-900/80 font-medium leading-relaxed italic">
                                {data.economicIndicators.summary}
                            </div>
                            {data.economicIndicators.imfOutlook && (
                                <div className="mt-4 p-3 bg-white/50 border border-blue-200 rounded-lg">
                                    <h6 className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-1 flex items-center gap-1">
                                        <span className="animate-pulse">●</span> {dailyMicroMode ? 'Economic Outlook Forecast' : 'IMF Economic Outlook Forecast'}
                                    </h6>
                                    <p className="text-[11px] text-blue-800 font-bold leading-snug">
                                        {data.economicIndicators.imfOutlook}
                                    </p>
                                </div>
                            )}
                            {!suppressSourceLinks && <div className="mt-4 flex gap-4">
                                <a href="https://data.worldbank.org" target="_blank" rel="noopener noreferrer" className="text-[10px] font-black text-blue-600 uppercase tracking-tighter hover:underline">World Bank Open Data &rarr;</a>
                                <a href="https://www.imf.org/en/Countries" target="_blank" rel="noopener noreferrer" className="text-[10px] font-black text-blue-600 uppercase tracking-tighter hover:underline">IMF Country Reports &rarr;</a>
                            </div>}
                        </div>
                    </div>
                </Section>
            )}

            {((!isUnitedStates && data.usEmbassy) || data.visaRequirements || isUnitedStates || whoMedicalSummary) && (
                <Section title="Diplomatic, Travel & Health Intelligence">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* US Embassy */}
                        {!isUnitedStates && data.usEmbassy && (
                            <div className="bg-slate-900 rounded-xl p-5 border border-slate-700 shadow-xl flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-blue-600 rounded-lg text-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-white uppercase tracking-wider">US Embassy Details</h4>
                                </div>
                                <div className="space-y-4 flex-grow">
                                    <div>
                                        <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest block mb-1">Office Location</span>
                                        <p className="text-xs text-slate-300 font-medium leading-relaxed">{data.usEmbassy.address || 'N/A'}</p>
                                    </div>
                                    <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                                        <span className="text-[9px] font-black text-red-400 uppercase tracking-widest block mb-1">Emergency Contact</span>
                                        <p className="text-sm font-black text-white">{data.usEmbassy.emergencyContact || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Visa Entry */}
                        {(data.visaRequirements || isUnitedStates) && (
                            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-indigo-100 rounded-lg text-indigo-600">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-slate-800 uppercase tracking-wider">Visa Requirements</h4>
                                </div>
                                <div className="space-y-3 flex-grow scrollbar-hide overflow-y-auto max-h-48 text-xs text-slate-600 leading-relaxed font-medium">
                                    {isUnitedStates ? US_ENTRY_SUMMARY : data.visaRequirements.summary}
                                </div>
                                {!suppressSourceLinks && <div className="mt-4 pt-4 border-t border-slate-100">
                                    <a 
                                        href={isUnitedStates ? US_VISA_URL : data.visaRequirements.stateDeptLink || "https://travel.state.gov/content/travel/en/international-travel/International-Travel-Country-Information-Pages.html"}
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[10px] font-black text-blue-600 uppercase tracking-widest hover:underline flex items-center gap-2"
                                    >
                                        State Dept Info
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </a>
                                </div>}
                            </div>
                        )}

                        {/* WHO Medical */}
                        {whoMedicalSummary && (
                            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100 shadow-sm flex flex-col">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-emerald-600 rounded-lg text-white">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                    </div>
                                    <h4 className="text-sm font-black text-emerald-900 uppercase tracking-wider">{dailyMicroMode ? 'Medical Advisory' : 'Medical & WHO Advisory'}</h4>
                                </div>
                                <div className="space-y-3 flex-grow text-xs text-emerald-800 leading-relaxed font-medium">
                                    <p>{whoMedicalSummary.summary}</p>
                                    {(whoMedicalSummary.lifeExpectancy || whoMedicalSummary.universalHealthCoverage) && (
                                        <div className="grid grid-cols-2 gap-2 pt-2">
                                            {whoMedicalSummary.lifeExpectancy && (
                                                <div className="rounded-lg bg-white/70 p-2 border border-emerald-100">
                                                    <span className="block text-[9px] font-black uppercase tracking-wider text-emerald-600">Life Expectancy</span>
                                                    <span className="font-black">{whoMedicalSummary.lifeExpectancy.value} years</span>
                                                    {whoMedicalSummary.lifeExpectancy.year && <span className="block text-[9px]">{dailyMicroMode ? whoMedicalSummary.lifeExpectancy.year : `WHO ${whoMedicalSummary.lifeExpectancy.year}`}</span>}
                                                </div>
                                            )}
                                            {whoMedicalSummary.universalHealthCoverage && (
                                                <div className="rounded-lg bg-white/70 p-2 border border-emerald-100">
                                                    <span className="block text-[9px] font-black uppercase tracking-wider text-emerald-600">UHC Index</span>
                                                    <span className="font-black">{whoMedicalSummary.universalHealthCoverage.value}</span>
                                                    {whoMedicalSummary.universalHealthCoverage.year && <span className="block text-[9px]">{dailyMicroMode ? whoMedicalSummary.universalHealthCoverage.year : `WHO ${whoMedicalSummary.universalHealthCoverage.year}`}</span>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {whoMedicalSummary.activeAlerts.length > 0 && (
                                        <div><strong>Active advisories:</strong> {whoMedicalSummary.activeAlerts.join('; ')}</div>
                                    )}
                                    {whoMedicalSummary.vaccinationGuidance.length > 0 && (
                                        <div><strong>Vaccination guidance:</strong> {whoMedicalSummary.vaccinationGuidance.join('; ')}</div>
                                    )}
                                </div>
                                {!suppressSourceLinks && <div className="mt-4 pt-4 border-t border-emerald-200">
                                    <a 
                                        href={whoMedicalSummary.whoLink} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-[10px] font-black text-emerald-700 uppercase tracking-widest hover:underline flex items-center gap-2"
                                    >
                                        WHO Country Page
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                    </a>
                                </div>}
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {data.religious_analysis && (
                <Section title="Religious and Cultural Analysis">
                    <div className="bg-white p-4 border rounded-lg shadow-sm border-l-4 border-l-indigo-500">
                        <div className="prose prose-slate prose-sm max-w-none text-slate-700 mb-4" dangerouslySetInnerHTML={{ __html: md.render(
                            typeof data.religious_analysis === 'string' ? data.religious_analysis : 
                            data.religious_analysis.summary || data.religious_analysis.overview || ''
                        )}} />
                        
                        {(data.religious_analysis.social || data.social) && (
                            <div className="mb-4 pt-4 border-t border-slate-100">
                                <h5 className="text-[10px] font-black text-indigo-700 uppercase mb-2 tracking-widest">Social & Cultural Architecture</h5>
                                <div className="text-xs text-slate-600 leading-relaxed italic" 
                                     dangerouslySetInnerHTML={{ __html: md.render(safeStr(data.religious_analysis.social) || safeStr(data.social) || '') }} />
                            </div>
                        )}

                        {data.religious_analysis.extremismRisk && (
                            <div className="p-3 bg-red-50 rounded border border-red-100">
                                <h5 className="text-xs font-bold text-red-800 uppercase mb-1">Extremism & Radicalization Risk</h5>
                                <p className="text-sm text-red-900">{data.religious_analysis.extremismRisk}</p>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {data.usInterests && (
                <Section title="US Diplomatic & Regional Interests">
                    <div className="bg-slate-900 text-slate-100 p-5 rounded-lg shadow-xl border border-slate-700">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-blue-600 rounded-lg">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h8a2 2 0 002-2v-1a2 2 0 012-2h1.945M7.881 4.026A9 9 0 1012 21a9 9 0 00-4.119-16.974M12 3c.83 0 1.623.114 2.37.322" /></svg>
                            </div>
                            <h4 className="text-xl font-bold">Strategic US Interests</h4>
                        </div>
                        <div className="prose prose-invert prose-sm max-w-none text-slate-300 mb-6" dangerouslySetInnerHTML={{ __html: md.render(data.usInterests.summary || '') }} />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                            {data.usInterests.keyObjectives?.map((obj: string, i: number) => (
                                <div key={i} className="flex gap-2 items-start bg-slate-800 p-3 rounded border border-slate-700">
                                    <span className="text-blue-400 font-bold">•</span>
                                    <span className="text-sm">{obj}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Section>
            )}
            
            {data.scenarios && (
                <Section title="Strategic Forecasting">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {(data.scenarios.mlcoa || data.mlcoa) && (
                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-col h-full">
                                <h5 className="font-bold text-blue-800 mb-2 text-[10px] uppercase tracking-widest">Most Likely (MLCOA)</h5>
                                <p className="font-bold text-slate-800 text-sm mb-2">{(data.scenarios.mlcoa || data.mlcoa).title || 'MLCOA'}</p>
                                <p className="text-xs text-slate-600 leading-relaxed flex-grow">
                                    {typeof (data.scenarios.mlcoa || data.mlcoa) === 'string' ? (data.scenarios.mlcoa || data.mlcoa) : (data.scenarios.mlcoa || data.mlcoa).description || 'No description available.'}
                                </p>
                            </div>
                        )}
                        {data.scenarios.mdcoa && (
                            <div className="bg-red-50 p-4 rounded-lg border border-red-100 flex flex-col h-full">
                                <h5 className="font-bold text-red-800 mb-2 text-[10px] uppercase tracking-widest">Most Dangerous (MDCOA)</h5>
                                <p className="font-bold text-slate-800 text-sm mb-2">{data.scenarios.mdcoa.title || 'MDCOA'}</p>
                                <p className="text-xs text-slate-600 leading-relaxed flex-grow">
                                    {typeof data.scenarios.mdcoa === 'string' ? data.scenarios.mdcoa : data.scenarios.mdcoa.description || 'No description available.'}
                                </p>
                            </div>
                        )}
                        {data.scenarios.bestCase && (
                            <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex flex-col h-full">
                                <h5 className="font-bold text-green-800 mb-2 text-[10px] uppercase tracking-widest">Best Case / Opportunity</h5>
                                <p className="font-bold text-slate-800 text-sm mb-2">{data.scenarios.bestCase.title || 'Best Case'}</p>
                                <p className="text-xs text-slate-600 leading-relaxed flex-grow">
                                    {typeof data.scenarios.bestCase === 'string' ? data.scenarios.bestCase : data.scenarios.bestCase.description || 'No description available.'}
                                </p>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            {/* NEW: CARVER & ASCOPE Analysis */}
            {(data.carverAssessment || data.ascope) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {data.carverAssessment && (
                        <Section title="CARVER Target Assessment">
                            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-4 h-full">
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                                    {['criticality', 'accessibility', 'recuperability', 'vulnerability', 'effect', 'recognizability'].map((key) => (
                                        <div key={key} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                                            <h5 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{key}</h5>
                                            <p className="text-[11px] text-slate-700 font-bold leading-tight">{(data.carverAssessment as any)[key]}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100 mt-2">
                                    <h5 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Targeting Outlook</h5>
                                    <p className="text-xs text-indigo-900 font-semibold italic">{data.carverAssessment.overallAssessment || 'Assessment pending operational data verification.'}</p>
                                </div>
                            </div>
                        </Section>
                    )}

                    {data.ascope && (
                        <Section title="ASCOPE Tactical Analysis">
                            <div className="bg-slate-900 rounded-xl p-6 border border-slate-800 shadow-xl space-y-3 h-full">
                                {Object.entries(data.ascope).map(([key, val]) => (
                                    <div key={key} className="border-b border-slate-800 pb-2 last:border-0 last:pb-0">
                                        <h5 className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">{key}</h5>
                                        <p className="text-[11px] text-slate-300 leading-relaxed">{typeof val === 'string' ? val : 'N/A'}</p>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    )}
                </div>
            )}

            {!suppressSourceLinks && data.sources && data.sources.filter((src: any) => {
                const url = src.url || src.uri || '';
                const name = src.name || src.sourceName || src.title || '';
                return name || (url && url !== 'N/A' && url.startsWith('http'));
            }).length > 0 && (
                <Section title="Intelligence Sources & References">
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                        <svg className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                        </svg>
                        <p className="text-[10px] text-amber-800 font-bold leading-tight">
                            DISCLAIMER: All OSINT and associated data sources is only relevent at the time of generation and can change rapidly due to geopolitical conditions and associated information reporting.
                        </p>
                    </div>
                    <div className="overflow-x-auto border border-slate-200 rounded-lg shadow-sm">
                        <table className="min-w-full text-xs text-left">
                            <thead className="bg-slate-50 text-slate-500 font-black uppercase">
                                <tr>
                                    <th className="px-4 py-2 border-b">Area</th>
                                    <th className="px-4 py-2 border-b">Source / Publisher</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {data.sources.map((src: any, i: number) => {
                                    const rawUrl = src.url || src.uri || '';
                                    const isValidUrl = rawUrl && rawUrl !== 'N/A' && rawUrl.startsWith('http');
                                    const displayName = src.name || src.sourceName || src.title || 'Official';
                                    if (!displayName && !isValidUrl) return null;
                                    return (
                                        <tr key={i} className="hover:bg-blue-50/30 transition-colors">
                                            <td className="px-4 py-2 font-bold text-slate-700">
                                                {(() => {
                                                    const rawArea = src.area || src.assessmentArea || 'General';
                                                    if (rawArea.includes(',') && rawArea.includes('PMESII')) {
                                                        return rawArea.split(',')[0].trim();
                                                    }
                                                    return rawArea;
                                                })()}
                                            </td>
                                            <td className="px-4 py-2 text-slate-600">{displayName}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            {data.healthData && (
                    <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                        <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                             <svg xmlns="http://www.w3.org" className="h-5 w-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                             Health & Medical
                        </h4>
                        <p className="text-sm text-slate-600 mb-2"><strong>Summary:</strong> {data.healthData.summary}</p>
                        {data.healthData.alerts && data.healthData.alerts.length > 0 && (
                             <div className="mb-2 bg-red-50 p-2 rounded text-xs text-red-700">
                                 <strong>Alerts:</strong> {data.healthData.alerts.join(', ')}
                             </div>
                        )}
                        <div className="text-xs text-slate-500 mb-3">
                            <p><strong>Vaccinations:</strong> {(data.healthData.vaccinations || []).join(', ') || 'Standard'}</p>
                        </div>
                        {!suppressSourceLinks && <a href="https://www.who.int" target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-blue-600 hover:text-blue-700 group flex items-center">
                            Go to WHO Website <span className="transition-transform group-hover:translate-x-1 ml-1">&rarr;</span>
                        </a>}
                    </div> 
                )}
            
            {data.travelAdvisory && (
                <div className="bg-white p-4 rounded border border-slate-200 shadow-sm">
                    <div className="flex justify-between items-start mb-3">
                        <h4 className="font-bold text-slate-800 flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2h8a2 2 0 002-2v-1a2 2 0 012-2h1.945M7.881 4.026A9 9 0 1012 21a9 9 0 00-4.119-16.974M12 3c.83 0 1.623.114 2.37.322" /></svg>
                            Entry & Travel Information
                        </h4>
                        {data.travelAdvisory.level && (
                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase text-white shadow-sm ${
                                data.travelAdvisory.level.toString().includes('4') ? 'bg-red-600' : 
                                data.travelAdvisory.level.toString().includes('3') ? 'bg-orange-500' : 
                                'bg-blue-600'
                            }`}>
                                Level: {data.travelAdvisory.level}
                            </span>
                        )}
                    </div>
                        <div className="space-y-2 text-sm text-slate-600 mb-3">
                            <strong>Advisory:</strong>
                            <div 
                                className="prose prose-sm max-w-none travel-advisory-content" 
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(data.travelAdvisory.summary || 'N/A') }}
                            />
                            <p><strong>Visa Policy:</strong> {data.entryRequirements?.tourist || data.visaRequirements?.tourist || 'N/A'}</p>
                        </div>
                        {!suppressSourceLinks && <div className="pt-2 border-t border-slate-100 flex justify-between items-center">
                            {data.travelAdvisory.url && data.travelAdvisory.url !== 'N/A' && data.travelAdvisory.url.startsWith('http') ? (
                                <a href={data.travelAdvisory.url} target="_blank" rel="noopener noreferrer" className="text-xs font-black uppercase text-blue-600 hover:text-blue-700 flex items-center gap-1">
                                    Official Advisory &rarr;
                                </a>
                            ) : (
                                <span className="text-[10px] text-slate-400 italic">No official advisory URL available</span>
                            )}
                            <a href="https://www.iatatravelcentre.com" target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-slate-500 hover:text-slate-700">
                                IATA Center
                            </a>
                        </div>}
                    </div>
                )}
            </div>
            
             {data.embassies && data.embassies.length > 0 && (
                <Section title="Diplomatic Missions">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {data.embassies.map((emb: any, i: number) => (
                            <div key={i} className="p-3 bg-slate-50 border rounded text-sm">
                                <p className="font-bold text-slate-800">{emb.country} Embassy</p>
                                <p className="text-slate-600 text-xs">{emb.location}</p>
                                <p className="text-blue-600 text-xs mt-1">{emb.contact}</p>
                            </div>
                        ))}
                    </div>
                    {!suppressSourceLinks && <div className="mt-4 text-xs">
                        <a href="https://www.embassypages.com/" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-600 hover:text-blue-700 group flex items-center">
                            Find Other Embassies <span className="transition-transform group-hover:translate-x-1 ml-1">&rarr;</span>
                        </a>
                    </div>}
                </Section>
            )}

            {data.riskFactors && data.riskFactors.length > 0 && (
                <Section title="Integrated Risk Matrix">
                    {/* OSINT Methodology Disclaimer */}
                    <div className="mb-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-[10px] text-amber-800 leading-relaxed">
                        <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                            <strong className="font-black">Methodology:</strong> Risk scores are automated estimates. Scores reflect conditions at generation time and can change. They are not substitutes for professional assessment.
                            {!dailyMicroMode && <button onClick={() => setOpenBrief('riskMatrix')} className="ml-2 inline-flex items-center gap-1 text-[10px] font-black text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors">Full methodology &amp; verification notice &#8594;</button>}
                        </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <div className="flex justify-end mb-4">
                            {!dailyMicroMode && <button
                                onClick={() => setOpenBrief('riskMatrix')}
                                className="flex items-center gap-1.5 text-[10px] font-black text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-wider"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                View Full Brief
                            </button>}
                        </div>
                        <RiskChart data={data.riskFactors} />
                        {!dailyMicroMode && <div className="mt-4 text-[10px] text-slate-400 font-bold uppercase tracking-widest text-right">
                            Metric Source: IntellOptics Risk Engine v4.2
                        </div>}
                    </div>

                </Section>
            )}

             {data.charts && data.charts.length > 0 && (
                <Section title="Summary Charts">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {data.charts.map((chart: any, index: number) => (
                            <div key={index} className="w-full h-80 bg-slate-50 p-4 rounded-lg border flex flex-col">
                                <h5 className="text-center font-semibold text-slate-600 mb-2 flex-shrink-0">{chart.title}</h5>
                                <div className="flex-grow w-full h-full">
                                    <ChartDisplay chartData={chart} />
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {(data.additionalSections || [])
                .filter((section: any) => {
                    const title = ((section as any).title || section.heading || '').toLowerCase();
                    const excludeList = [
                        'government stability','predictive analysis','strategic outlook','pmesii','scenario:',
                        'strategic forecasting','analyst confidence','executive summary','leadership','governance',
                        'risk factors','intelligence feed','ascope','embassies','diplomatic missions','charts',
                        'summary charts','threat groups','regional intelligence alert','political landscape',
                        'sources','references','quantitative indicators','systemic institutional framework'
                    ];
                    
                    // Only exclude if we have dedicated data for these
                    if (data.healthData) excludeList.push('health');
                    if (data.travelAdvisory) excludeList.push('travel advisory');
                    if (data.entryRequirements || data.visaRequirements) {
                        excludeList.push('entry requirements');
                        excludeList.push('visa requirements');
                    }

                    return !excludeList.some(term => title.includes(term));
                })
                .map((section: any, index: number) => (
                    <Section key={index} title={(section as any).title || section.heading || 'Analysis Section'}>
                        <div className="prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: md.render(section.content || '') }} />
                    </Section>
                ))}

            {!dailyMicroMode && data.threatGroups && data.threatGroups.length > 0 && (
                <Section title="Threat Group Intelligence — Full Spectrum Analysis" className="report-map-threat-section">
                    {/* Brief Button */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex flex-wrap gap-3">
                            {[
                                { color: '#ef4444', label: 'Terrorist / Extremist' },
                                { color: '#f97316', label: 'Criminal / Cartel' },
                                { color: '#eab308', label: 'Insurgent / Separatist' },
                                { color: '#a855f7', label: 'Militia / State-Proxy' },
                                { color: '#94a3b8', label: 'Other / Unknown' },
                            ].map(({ color, label }) => (
                                <div key={label} className="flex items-center gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full border border-white/20" style={{ backgroundColor: color }} />
                                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => setOpenBrief('threatGroups')}
                            className="flex items-center gap-1.5 text-[10px] font-black text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors uppercase tracking-wider shrink-0"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                            View Full OSINT Brief
                        </button>
                    </div>


                    {/* 60/40 Map + Briefing Panel */}
                    <div className="report-map-threat-layout flex flex-col lg:flex-row h-[720px] rounded-xl overflow-hidden border border-slate-700 shadow-2xl bg-slate-950">
                        {/* ── 60% Map ── */}
                        <div className="report-map-threat-map w-full lg:w-[60%] relative">
                            <UnifiedMap
                                isLoaded={isLoaded}
                                initialViewState={{
                                    longitude: tgCenter.lng,
                                    latitude: tgCenter.lat,
                                    zoom: tgZoom
                                }}
                                focusedLocation={tgFocusedLocation}
                                markers={[...tgMapMarkers, ...tgDetailMarkers]}
                                heatmapPoints={tgHeatPoints.length > 0 ? tgHeatPoints : undefined}
                                theme="dark"
                                interactive={true}
                                onNavigate={onNavigate}
                            />
                            <div className="absolute top-3 left-3 z-10 pointer-events-none">
                                <div className="bg-black/70 backdrop-blur-sm text-white text-[9px] font-black px-2 py-1 rounded border border-white/10 uppercase tracking-widest">
                                    ⚠ Active Threat Operations Map
                                </div>
                            </div>
                            {tgHeatPoints.length > 0 && (
                                <div className="absolute bottom-3 left-3 z-10 pointer-events-none">
                                    <div className="bg-black/60 text-slate-400 text-[8px] font-bold px-2 py-1 rounded uppercase tracking-wider">
                                        🔥 Heat: recent incident density
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* ── 40% Briefing Panel ── */}
                        <div className="report-map-threat-list w-full lg:w-[40%] bg-slate-950 border-l border-slate-800 flex flex-col overflow-hidden">
                            {selectedThreatGroup ? (
                                /* ── Selected Group Dossier ── */
                                <div className="analysis-screen-only flex flex-col h-full">
                                    <div
                                        className="p-4 border-b border-slate-800 flex items-start justify-between bg-slate-900"
                                        style={{ borderLeftWidth: 4, borderLeftColor: getThreatGroupColor(selectedThreatGroup.type) }}
                                    >
                                        <div className="flex-grow min-w-0">
                                            <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Intelligence Dossier</div>
                                            <h4 className="text-sm font-black text-white leading-tight truncate">{selectedThreatGroup.name}</h4>
                                            <div className="flex gap-2 mt-1.5 flex-wrap">
                                                <span className="text-[9px] font-black px-2 py-0.5 rounded uppercase border"
                                                    style={{ backgroundColor: `${getThreatGroupColor(selectedThreatGroup.type)}22`, color: getThreatGroupColor(selectedThreatGroup.type), borderColor: `${getThreatGroupColor(selectedThreatGroup.type)}44` }}>
                                                    {selectedThreatGroup.type}
                                                </span>
                                                {selectedThreatGroup.threatLevel && (
                                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${
                                                        selectedThreatGroup.threatLevel === 'Critical' ? 'bg-red-900/50 text-red-300 border border-red-800' :
                                                        selectedThreatGroup.threatLevel === 'High' ? 'bg-orange-900/50 text-orange-300 border border-orange-800' :
                                                        'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                                                    }`}>
                                                        {selectedThreatGroup.threatLevel} Threat
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setSelectedThreatGroup(null)}
                                            className="text-slate-500 hover:text-white p-1 transition-colors ml-2 shrink-0"
                                            title="Back to group list"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                        </button>
                                    </div>

                                    <div className="flex-grow overflow-y-auto p-4 space-y-4 custom-scrollbar text-slate-300">
                                        {selectedThreatGroup.summary && (
                                            <div>
                                                <div className="text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1.5">Overview</div>
                                                <p className="text-xs leading-relaxed">{selectedThreatGroup.summary}</p>
                                            </div>
                                        )}
                                        {selectedThreatGroup.historicalContext && (
                                            <div>
                                                <div className="text-[9px] font-black text-indigo-400 uppercase tracking-widest mb-1.5">Historical Context</div>
                                                <p className="text-xs leading-relaxed text-slate-400">{selectedThreatGroup.historicalContext}</p>
                                            </div>
                                        )}
                                        {selectedThreatGroup.currentStructure && (
                                            <div className="bg-slate-900 rounded-lg p-3 border border-slate-800">
                                                <div className="text-[9px] font-black text-amber-400 uppercase tracking-widest mb-1.5">Current Structure & Strength</div>
                                                <p className="text-xs leading-relaxed text-slate-400">{selectedThreatGroup.currentStructure}</p>
                                            </div>
                                        )}
                                        {selectedThreatGroup.keyLeaders && selectedThreatGroup.keyLeaders.length > 0 && (
                                            <div>
                                                <div className="text-[9px] font-black text-emerald-400 uppercase tracking-widest mb-2">Current Leadership</div>
                                                <div className="space-y-2">
                                                    {selectedThreatGroup.keyLeaders.map((leader: any, i: number) => (
                                                        <div key={i} className="bg-slate-900 rounded-lg p-3 border border-slate-800 flex items-start gap-2.5">
                                                            <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-xs font-black text-slate-400 shrink-0 uppercase">
                                                                {(leader.name || '?').charAt(0)}
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="text-xs font-black text-white truncate">{leader.name}</div>
                                                                <div className="text-[9px] font-bold text-emerald-400 uppercase tracking-wider">{leader.role}</div>
                                                                {leader.bio && <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">{leader.bio}</p>}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {selectedThreatGroup.areaOfOperations && (
                                            <div>
                                                <div className="text-[9px] font-black text-red-400 uppercase tracking-widest mb-1.5">Area of Operations</div>
                                                <p className="text-xs leading-relaxed text-slate-400">
                                                    {typeof selectedThreatGroup.areaOfOperations === 'string'
                                                        ? selectedThreatGroup.areaOfOperations
                                                        : selectedThreatGroup.areaOfOperations.description || 'See map for geographic coverage'}
                                                </p>
                                            </div>
                                        )}
                                        {selectedThreatGroup.recentActivities && selectedThreatGroup.recentActivities.length > 0 && (
                                            <div>
                                                <div className="text-[9px] font-black text-orange-400 uppercase tracking-widest mb-2">Recent Activity Timeline</div>
                                                <div className="space-y-2">
                                                    {selectedThreatGroup.recentActivities.slice(0, 6).map((act: any, i: number) => (
                                                        <div key={i} className="flex gap-2.5 p-2 bg-slate-900/60 rounded border border-slate-800">
                                                            <div className="shrink-0 text-[8px] font-black text-orange-400 pt-0.5 min-w-[52px]">{formatLocalDate(act.date, 'RECENT')}</div>
                                                            <div>
                                                                <div className="text-[9px] font-black text-slate-300 uppercase tracking-wider">{act.type}</div>
                                                                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{act.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {selectedThreatGroup.pmesiiAnalysis && (
                                            <div>
                                                <div className="text-[9px] font-black text-purple-400 uppercase tracking-widest mb-2">PMESII Analysis</div>
                                                <div className="space-y-1.5">
                                                    {Object.entries(selectedThreatGroup.pmesiiAnalysis).map(([key, val]) => (
                                                        <div key={key} className="bg-slate-900 rounded p-2 border border-slate-800">
                                                            <div className="text-[8px] font-black text-purple-400 uppercase tracking-widest mb-0.5 capitalize">{key}</div>
                                                            <p className="text-[10px] text-slate-400 leading-tight">{typeof val === 'string' ? val : JSON.stringify(val)}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {selectedThreatGroup.carverAssessment && (
                                            <div>
                                                <div className="text-[9px] font-black text-pink-400 uppercase tracking-widest mb-2">CARVER Assessment</div>
                                                <div className="grid grid-cols-2 gap-1.5">
                                                    {['criticality','accessibility','recuperability','vulnerability','effect','recognizability'].map(key => (
                                                        <div key={key} className="bg-slate-900 rounded p-2 border border-slate-800">
                                                            <div className="text-[8px] font-black text-pink-400 uppercase tracking-widest capitalize">{key}</div>
                                                            <p className="text-[10px] text-slate-300 font-bold mt-0.5">{(selectedThreatGroup.carverAssessment as any)[key] || 'N/A'}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                                {selectedThreatGroup.carverAssessment.overallAssessment && (
                                                    <div className="mt-2 p-2 bg-pink-950/40 rounded border border-pink-900/30">
                                                        <p className="text-[10px] text-pink-300 italic leading-relaxed">{selectedThreatGroup.carverAssessment.overallAssessment}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {onNavigate && (
                                            <button
                                                onClick={() => onNavigate({ page: 'gtg_dashboard', params: { initialSearch: selectedThreatGroup.name } })}
                                                className="w-full text-[10px] font-black uppercase text-blue-400 hover:text-blue-300 bg-blue-950/40 border border-blue-900/40 hover:bg-blue-900/40 py-2 rounded transition-all tracking-widest"
                                            >
                                                Open Full OSINT Threat Data Hub →
                                            </button>
                                        )}
                                        <button
                                            onClick={() => { setBriefDetailGroup(selectedThreatGroup); setOpenBrief('threatGroupDetail'); }}
                                            className="w-full text-[10px] font-black uppercase text-emerald-400 hover:text-emerald-300 bg-emerald-950/40 border border-emerald-900/40 hover:bg-emerald-900/40 py-2 rounded transition-all tracking-widest"
                                        >
                                            View Full Page Brief / Download PDF →
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* ── Group Selection List ── */
                                <div className="analysis-screen-only flex flex-col h-full">
                                    <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60">
                                        <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Select a group to view intelligence dossier</div>
                                        <div className="text-[8px] text-slate-600 mt-0.5">{tgGroups.length} threat actor{tgGroups.length !== 1 ? 's' : ''} identified</div>
                                    </div>
                                    <div className="flex-grow overflow-y-auto custom-scrollbar">
                                        {tgGroups.map((group: any, idx: number) => {
                                            const color = getThreatGroupColor(group.type);
                                            const lat = group.areaOfOperations?.centerLat ?? group.areaOfOperations?.coordinates?.lat ?? group.areaOfOperations?.lat;
                                            const lng = group.areaOfOperations?.centerLng ?? group.areaOfOperations?.coordinates?.lng ?? group.areaOfOperations?.lng;
                                            return (
                                                <button
                                                    key={group.name || idx}
                                                    onClick={() => {
                                                        setSelectedThreatGroup(group);
                                                        if (typeof lat === 'number' && typeof lng === 'number') {
                                                            setTgFocusedLocation({ lat, lng, zoom: 7 });
                                                        }
                                                    }}
                                                    className="w-full text-left p-3 border-b border-slate-800/50 hover:bg-slate-900 transition-all group"
                                                >
                                                    <div className="flex items-start gap-2.5">
                                                        <div className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 border border-white/15" style={{ backgroundColor: color }} />
                                                        <div className="flex-grow min-w-0">
                                                            <div className="flex justify-between items-start gap-2">
                                                                <span className="text-xs font-black text-white group-hover:text-blue-300 transition-colors truncate">{group.name}</span>
                                                                {group.threatLevel && (
                                                                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded uppercase shrink-0"
                                                                        style={{ backgroundColor: `${color}22`, color }}>
                                                                        {group.threatLevel}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">{group.type}</div>
                                                            {group.summary && (
                                                                <p className="text-[10px] text-slate-600 mt-1 leading-tight line-clamp-2">{group.summary}</p>
                                                            )}
                                                        </div>
                                                        <svg className="w-3.5 h-3.5 text-slate-600 group-hover:text-slate-400 transition-colors shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="analysis-print-only p-4 text-slate-200">
                                <h5 className="mb-3 text-xs font-bold uppercase text-blue-300">Threat groups ({tgGroups.length})</h5>
                                <div className="space-y-3">
                                    {tgGroups.map((group: any, index: number) => (
                                        <div key={group.name || index} className="report-print-threat-item rounded-lg border border-slate-700 p-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="font-bold text-white">{group.name}</span>
                                                {group.threatLevel && <span className="text-xs font-bold uppercase text-amber-300">{group.threatLevel}</span>}
                                            </div>
                                            {group.type && <p className="mt-1 text-xs uppercase text-slate-400">{group.type}</p>}
                                            {group.summary && <p className="mt-2 text-sm leading-relaxed">{group.summary}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>
            )}

            
            {data.recommendedNextSteps && data.recommendedNextSteps.length > 0 && (
                <RecommendedSuitesDisplay 
                    suites={data.recommendedNextSteps} 
                    onNavigate={onNavigate} 
                    contextCountry={data.country || result.country}
                />
            )}

            {/* ── RECOMMENDATIONS & NEXT STEPS ── */}
            {data.recommendations && (data.recommendations.immediate?.length > 0 || data.recommendations.strategic?.length > 0 || data.recommendations.optional?.length > 0) && (
                <Section title="Recommendations &amp; Next Steps">
                    <div className="space-y-6">

                        {/* Immediate Actions */}
                        {data.recommendations.immediate?.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                                    <h5 className="text-[10px] font-black text-red-600 uppercase tracking-widest">Immediate Priority Actions</h5>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {data.recommendations.immediate.map((item: any, i: number) => (
                                        <div key={i} className="p-4 bg-red-50 border border-red-100 rounded-xl border-l-4 border-l-red-500 shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                                                    item.priority === 'Critical' ? 'bg-red-600 text-white' :
                                                    item.priority === 'High' ? 'bg-orange-500 text-white' :
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>{item.priority || 'High'}</span>
                                                {item.timeline && <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{item.timeline}</span>}
                                            </div>
                                            <p className="text-sm font-black text-slate-900 mb-1">{item.action}</p>
                                            {item.rationale && <p className="text-[11px] text-slate-600 italic leading-snug">{item.rationale}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Strategic Recommendations */}
                        {data.recommendations.strategic?.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="h-2 w-2 rounded-full bg-blue-500" />
                                    <h5 className="text-[10px] font-black text-blue-700 uppercase tracking-widest">Strategic Recommendations</h5>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {data.recommendations.strategic.map((item: any, i: number) => (
                                        <div key={i} className="p-4 bg-blue-50 border border-blue-100 rounded-xl border-l-4 border-l-blue-500 shadow-sm">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                                                    item.priority === 'High' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
                                                }`}>{item.priority || 'Medium'}</span>
                                                {item.timeline && <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{item.timeline}</span>}
                                            </div>
                                            <p className="text-sm font-black text-slate-900 mb-1">{item.action}</p>
                                            {item.rationale && <p className="text-[11px] text-slate-600 italic leading-snug">{item.rationale}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Optional Next Steps */}
                        {data.recommendations.optional?.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                    <h5 className="text-[10px] font-black text-emerald-700 uppercase tracking-widest">Optional Enhancements</h5>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {data.recommendations.optional.map((item: any, i: number) => (
                                        <div key={i} className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                            {item.category && (
                                                <span className="text-[9px] font-black text-emerald-600 uppercase tracking-widest block mb-1">{item.category}</span>
                                            )}
                                            <p className="text-sm font-black text-slate-900 mb-1">{item.action}</p>
                                            {item.rationale && <p className="text-[11px] text-slate-600 italic leading-snug">{item.rationale}</p>}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ECC Workflow Next Steps */}
                        {data.recommendations.nextStepWorkflows?.length > 0 && onNavigate && (
                            <div className="pt-4 border-t border-slate-200">
                                <h5 className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-3">Suggested ECC Workflows</h5>
                                <div className="flex flex-wrap gap-2">
                                    {data.recommendations.nextStepWorkflows.map((wf: string, i: number) => (
                                        <button
                                            key={i}
                                            onClick={() => onNavigate(readOnly
                                                ? { page: 'serviceRequests' }
                                                : { page: 'suiteDashboard', params: { suiteName: 'Geopolitical Risk Management', workflow: wf } })}
                                            className="px-4 py-2 bg-slate-900 hover:bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg transition-all shadow-sm hover:shadow-indigo-500/30 hover:shadow-lg active:scale-95 flex items-center gap-2"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                            {wf}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </Section>
            )}

            <Disclaimer text={data.disclaimer} />

            <div className="mt-12 text-center pb-8">
                <div className="inline-flex flex-col items-center">
                    <div className="h-px w-24 bg-slate-300 mb-6"></div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-2">Authenticated Intelligence Product</div>
                    <div className="text-[9px] text-slate-400 font-medium max-w-md mx-auto leading-normal text-center">
                        Disclaimer: This report is generated based on real-time intelligence telemetry and historical risk modeling. For operational use cases, consult with a lead analyst.
                        <br/>© 2026 IntellOptics Global Security. All Rights Reserved.
                    </div>
                </div>
            </div>
        </div> 
        {/* Section Brief Modals */}
        {!dailyMicroMode && openBrief && (
            <SectionBriefModal
                briefType={openBrief}
                data={data}
                result={result}
                onClose={() => { setOpenBrief(null); setBriefDetailGroup(null); }}
                getThreatGroupColor={getThreatGroupColor}
                detailGroup={briefDetailGroup}
                onNavigate={onNavigate}
            />
        )}
        </>
    );     
};

const InfrastructureInventory: React.FC<{ data: any[] }> = ({ data }) => {
    const sectors = Array.from(new Set(data.map(i => i.type)));
    
    return (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center">
                <h4 className="text-xs font-black uppercase text-slate-800 tracking-wider">Strategic Infrastructure Inventory</h4>
                <span className="text-[10px] font-bold text-slate-500">{data.length} Assets Identified</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto custom-scrollbar">
                {sectors.map(sector => (
                    <div key={sector} className="space-y-2">
                        <div className="text-[10px] font-black text-blue-600 uppercase border-b border-blue-100 pb-1">{sector}</div>
                        {data.filter(i => i.type === sector).map((item, idx) => (
                            <div key={idx} className="bg-slate-50/50 p-2 rounded border border-slate-100">
                                <div className="text-xs font-bold text-slate-800">{item.name}</div>
                                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">{item.description}</p>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const QuantitativeIndicatorsDisplay: React.FC<{ indicators: any }> = ({ indicators }) => {
    const groups = quantitativeIndicatorGroups(indicators);
    if (!groups.length) return null;
    
    return (
        <div className="report-quantitative-indicators report-print-keep-together bg-white border-2 border-slate-900 rounded-xl overflow-hidden shadow-xl mb-6">
            <div className="report-indicator-header bg-slate-900 text-white px-6 py-4 flex justify-between items-center">
                <div>
                    <h3 className="text-sm font-black uppercase tracking-[0.2em]">Quantitative Risk Indicators</h3>
                    <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Reference Metrics · Not Normalized Risk Scores</p>
                </div>
                <div className="h-10 w-10 bg-white/10 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                </div>
            </div>
            <div className="report-indicator-grid p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groups.map(group => (
                    <div key={group.title} className="report-indicator-group min-w-0 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 pb-2">{group.title}</h4>
                        {group.rows.map(row => (
                            <div key={row.label} className="report-indicator-row flex flex-col items-start gap-1 border-b border-slate-200 pb-2 last:border-b-0 last:pb-0">
                                <span className="text-[11px] font-bold text-slate-600">{row.label}</span>
                                <span className="max-w-full break-words text-left text-sm font-black tabular-nums text-slate-900">
                                    {row.value}
                                    {row.period && <span className="block text-[9px] font-bold text-slate-400">{row.period}</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

const ChartDisplay: React.FC<{ chartData: ChartItem }> = ({ chartData }) => {
    const { chartType, data, config } = chartData;
    const { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line, PieChart, Pie, Cell } = require('recharts');

    const renderChart = () => {
        switch (chartType) {
            case 'bar':
                return (
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={config.xAxisKey} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {config.dataKeys.map((key: string, index: number) => (
                            <Bar key={key} dataKey={key} fill={config.colors?.[index] || '#4f46e5'} />
                        ))}
                    </BarChart>
                );
            case 'line':
                return (
                    <LineChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={config.xAxisKey} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {config.dataKeys.map((key: string, index: number) => (
                            <Line key={key} type="monotone" dataKey={key} stroke={config.colors?.[index] || '#4f46e5'} />
                        ))}
                    </LineChart>
                );
            case 'pie':
                return (
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey={config.dataKeys[0]}
                        >
                            {data.map((entry: any, index: number) => (
                                <Cell key={`cell-${index}`} fill={config.colors?.[index % config.colors.length] || '#4f46e5'} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                );
            default:
                return <div className="flex items-center justify-center h-full text-slate-400">Unsupported chart type: {chartType}</div>;
        }
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
        </ResponsiveContainer>
    );
};

export default GeopoliticalAnalysisDisplay;
